import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Chip,
    Divider,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    IconButton,
    Alert,
    TextField,
    BottomNavigation,
    BottomNavigationAction,
    Switch,
    AppBar,
    Toolbar,
    Menu,
    MenuItem,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Paper,
    Tabs,
    Tab,
    Select,
    FormControl,
    Fab,
    Snackbar,
    Badge,
    Popover,
} from '@mui/material';
import {
    AccountCircle,
    ExitToApp,
    Business as BusinessIcon,
    Add,
    Close,
    Inventory,
    People,
    ExpandMore,
    ExpandLess,
    CalendarMonth,
    CurrencyRupee,
    Dashboard as DashboardIcon,
    Edit,
    Receipt,
    Group as GroupIcon,
    Today as TodayIcon,
    Map as MapIcon,
    WhatsApp as WhatsAppIcon,
    Phone,
    HelpOutline,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    Translate as TranslateIcon,
    ModeEdit as ModeEditIcon,
    Autorenew as AutorenewIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
    ShoppingCart as OrdersIcon,
    ArrowDropDown as ArrowDropDownIcon,
    LocalOffer as LocalOfferIcon,
    Share as ShareIcon,
    Delete as DeleteIcon,
    Check as CheckIcon,
    Remove,
    Search as SearchIcon,
    Clear as ClearIcon,
    LocationOn,
    PhotoCamera,
    DragIndicator,
    FormatListNumbered,
} from '@mui/icons-material';
import {
    DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { authAPI, businessAPI, notificationAPI } from '../api';
import ProductImage from './common/ProductImage';
import ListCard from './common/ListCard';
import CardActions from './common/CardActions';
import MetaLine from './common/MetaLine';
import { GRIP_W, TOUCH, WHATSAPP } from '../theme/tokens';
import BusinessForm from './BusinessForm';

import ProductFormDialog from './ProductFormDialog';
import ShareProductsDialog from './ShareProductsDialog';
import SharedProductsDialog from './SharedProductsDialog';
import AreaFilterRow from './common/AreaFilterRow';
import { CUSTOMER_SORT_OPTIONS, sortCustomers, addedLabel, SORT_NEWEST } from './common/CustomerSort';
import SortSelect from './common/SortSelect';
import { sortAreasByRoute } from '../utils/routeOrder';
import CustomerManager from './CustomerManager';
import CustomerSubscriptionManager from './CustomerSubscriptionManager';
import PlanFormDialog from './PlanFormDialog';
import { APP_DIALOG, APP_DIALOG_TITLE_SX, DIALOG_TITLE_SX } from '../utils/dialogStyle';
import BillingPage from './BillingPage';

import DailyOpsPage from './DailyOpsPage';
import GpsSelector from './GpsSelector';
import DuesPage from './DuesPage';
import OrdersPage from './OrdersPage';
import OrdersSummaryPage from './OrdersSummaryPage';
import OrderPaymentAlerts from './OrderPaymentAlerts';
import SuperAdminDashboard from './SuperAdminDashboard';
import ProductPriceDialog from './ProductPriceDialog';
import ProductDiscountDialog from './ProductDiscountDialog';
import ProductCategoryManager from './ProductCategoryManager';
import { alpha } from '@mui/material/styles';
import AssignCustomersDialog from './AssignCustomersDialog';
import DeletedItemsPage from './DeletedItemsPage';
import { SUPPORT_PHONE_DISPLAY, supportCallHref, supportWhatsAppHref } from '../utils/support';
import CataloguePicker from './CataloguePicker';
import BulkPriceDialog from './BulkPriceDialog';
import DeliveryOrderDialog from './DeliveryOrderDialog';
import { productCategoryList } from '../utils/groupByCategory';
import CustomerDashboard from './customer/CustomerDashboard';
import InstallPromptBanner from './InstallPromptBanner';

// ── Session persistence ──────────────────────────────────────────────────────
// Remember the last view/business/tab so reopening the app resumes where the
// user left off. Stored per-user; cleared on logout.
const SESSION_KEY = 'vendora.session';
const loadSession = () => {
    try { return JSON.parse(localStorage.getItem(SESSION_KEY)) || {}; } catch { return {}; }
};
const saveSession = (patch) => {
    try {
        const cur = JSON.parse(localStorage.getItem(SESSION_KEY)) || {};
        localStorage.setItem(SESSION_KEY, JSON.stringify({ ...cur, ...patch }));
    } catch { /* ignore */ }
};
export const CUSTOMER_TAB_KEY = 'vendora.customerTab';
export const clearSession = () => {
    try {
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(CUSTOMER_TAB_KEY);
    } catch { /* ignore */ }
};

// ── Area drag-to-reorder ─────────────────────────────────────────────────────
// Category chip value for "products in no category". Category names are free text, so this
// is a value no vendor would type as a name.
const NO_CATEGORY_FILTER = '__no_category__';

// How many customers are currently on a product or plan, as a small pill inside its Customers
// button. `onPrimary` is for the solid blue button, where the pill has to be light on dark.
const CustomerCount = ({ n, onPrimary = false }) => (
    <Box component="span" sx={{
        ml: 0.5, px: 0.6, minWidth: 18, borderRadius: 999, lineHeight: 1.5,
        fontSize: '0.7rem', fontWeight: 700, textAlign: 'center', display: 'inline-block',
        bgcolor: onPrimary ? 'rgba(255,255,255,0.25)' : 'action.selected',
        color: 'inherit',
    }}>
        {n || 0}
    </Box>
);

// Wraps an area card so the vendor can drag it into the order the round covers.
// Drag is disabled for a lone area — there is nothing to reorder.
//
// The listeners sit on the grip, not the card: a draggable card would have to allow panning so
// the page could still scroll, and the browser then claims a vertical swipe as a scroll before
// the drag ever starts. `touch-action: none` on the grip alone keeps both gestures working.
const SortableAreaCard = ({ id, disabled, children }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
        useSortable({ id, disabled });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <ListCard ref={setNodeRef} style={style} active={isDragging}
            sx={{ userSelect: 'none' }}
            leading={!disabled && (
                <Box ref={setActivatorNodeRef} {...attributes} {...listeners}
                    sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: GRIP_W, flexShrink: 0,
                        touchAction: 'none',
                        color: 'text.disabled',
                        cursor: 'grab', '&:active': { cursor: 'grabbing' },
                    }}>
                    <DragIndicator />
                </Box>
            )}>
            {children}
        </ListCard>
    );
};

// "No area" in the Add Customers area filter (area ids are numbers, so this can't clash).
const PLAN_NO_AREA = '__no_area__';

const Dashboard = ({ onLogout, themeMode, onToggleTheme }) => {
    const { t } = useTranslation();
    const [previewImg, setPreviewImg] = useState(null);
    const [user, setUser] = useState(null);
    const [businesses, setBusinesses] = useState([]);
    const [selectedBusinessId, setSelectedBusinessId] = useState(null);
    const [businessProducts, setBusinessProducts] = useState([]);
    const [businessCustomers, setBusinessCustomers] = useState([]);
    const [, setAllCategories] = useState([]);
    const [activeTab, setActiveTab] = useState(() => {
        const t = loadSession().vendorTab;
        return Number.isInteger(t) ? t : 0;
    });
    const [loading, setLoading] = useState(true);
    const [showBusinessForm, setShowBusinessForm] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [priceDialogProduct, setPriceDialogProduct] = useState(null);
    const [discountDialogProduct, setDiscountDialogProduct] = useState(null);
    const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [productSortBy, setProductSortBy] = useState('my_order');
    const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
    const [helpAnchorEl, setHelpAnchorEl] = useState(null);   // top-bar Help menu
    // Vendor's saved category order (Sort & Categories), for the "Category" product sort
    const [productCategoryOrder, setProductCategoryOrder] = useState([]);
    const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
    const [cataloguePickerOpen, setCataloguePickerOpen] = useState(false);
    const [quickProductOpen, setQuickProductOpen] = useState(false);
    const [archiveProductTarget, setArchiveProductTarget] = useState(null);  // product to archive
    const [archiveProductActivePlans, setArchiveProductActivePlans] = useState(0);
    const [archiveProductLoading, setArchiveProductLoading] = useState(false);
    const [archiveProductChecking, setArchiveProductChecking] = useState(false);
    const [archiveProductError, setArchiveProductError] = useState('');
    const [archiveProductHolderIds, setArchiveProductHolderIds] = useState([]);  // customers still on it
    const [archiveEndingMode, setArchiveEndingMode] = useState(null);  // 'end' | 'end_delete' while saving
    // Bill months still open, up to this one — what a delete would take off the bill.
    const [archiveOpenMonths, setArchiveOpenMonths] = useState([]);
    // One-line "done" message after a delete, so the vendor knows it worked.
    const [deleteNotice, setDeleteNotice] = useState('');
    // Product → bulk add customer plans dialog
    const [planProductId, setPlanProductId] = useState(null);          // product whose dialog is open
    const [planProductSubs, setPlanProductSubs] = useState([]);         // existing active sub customer IDs for this product
    const [planProductLoading, setPlanProductLoading] = useState(false);
    const [planProductSearch, setPlanProductSearch] = useState('');
    const [planSort, setPlanSort] = useState(SORT_NEWEST);   // newest first: just-added customers lead
    const [planProductPage, setPlanProductPage] = useState(50);
    const [planSelectedIds, setPlanSelectedIds] = useState([]);         // customer IDs selected for bulk add
    const [planCustomerQty, setPlanCustomerQty] = useState({});         // per-customer qty overrides
    const [planQuantity, setPlanQuantity] = useState(1);                // default qty
    const [planStartDate, setPlanStartDate] = useState('');
    const [planAdding, setPlanAdding] = useState(false);
    const [planAddResult, setPlanAddResult] = useState(null);           // { added, failed }
    const [planAreaFilter, setPlanAreaFilter] = useState('');            // '' | area id | PLAN_NO_AREA
    const [planFilterBy, setPlanFilterBy] = useState('no_plans');        // null | 'no_plans' | 'other_products'
    const [planAllSubCustIds, setPlanAllSubCustIds] = useState(new Set());
    const [planAssignedExpanded, setPlanAssignedExpanded] = useState(false);
    const [planAssignedPage, setPlanAssignedPage] = useState(50);
    const [bizAnchorEl, setBizAnchorEl] = useState(null);
    const [profileAnchorEl, setProfileAnchorEl] = useState(null);
    const [settingsSubTab, setSettingsSubTab] = useState(0);
    // Plans live inside the Products list — { product } to create one, { plan } to edit one.
    const [planForm, setPlanForm] = useState(null);
    const [planOpsError, setPlanOpsError] = useState('');
    const [businessMembers, setBusinessMembers] = useState([]);
    // Removing an agent: { member, count, moveTo } — their customers have to go somewhere.
    const [memberRemove, setMemberRemove] = useState(null);
    const [memberRemoving, setMemberRemoving] = useState(false);
    const [businessAreas, setBusinessAreas] = useState([]);
    // Inline area management
    const [areaShowAddForm, setAreaShowAddForm] = useState(false);
    const [newAreaName, setNewAreaName] = useState('');
    const [areaEditingId, setAreaEditingId] = useState(null);
    const [areaEditName, setAreaEditName] = useState('');
    const [areaOpsLoading, setAreaOpsLoading] = useState(false);
    const [areaOpsError, setAreaOpsError] = useState('');
    const [newAreaDeliveryCharge, setNewAreaDeliveryCharge] = useState('');
    const [editAreaDeliveryCharge, setEditAreaDeliveryCharge] = useState('');
    // GPS state for add form
    const [addAreaPicked, setAddAreaPicked] = useState(null);
    // GPS state for edit form
    const [editAreaPicked, setEditAreaPicked] = useState(null);
    const [editAreaExistingName, setEditAreaExistingName] = useState(null);
    const [editAreaCleared, setEditAreaCleared] = useState(false);
    // Area → customer assignment panel
    const [areaAssignId, setAreaAssignId] = useState(null);   // which area's dialog is open
    // Delivery order dialog — holds the area whose round is being arranged.
    // { id, name } with id === null for the customers that belong to no area.
    const [routeOrderArea, setRouteOrderArea] = useState(null);
    // Agent → customer assignment dialog
    const [agentAssignId, setAgentAssignId] = useState(null);
    // Area filter inside the assign dialog. '' = every area, 0 = customers with no area.
    // An agent's round is an area, so this is how a vendor actually picks who is theirs.
    // Inline member/agent management
    const [memberShowAddForm, setMemberShowAddForm] = useState(false);
    const [memberMobileInput, setMemberMobileInput] = useState('');
    const [memberNameInput, setMemberNameInput] = useState('');
    const [memberLookupResult, setMemberLookupResult] = useState(null);
    const [memberLookupError, setMemberLookupError] = useState('');
    const [memberLookupLoading, setMemberLookupLoading] = useState(false);
    const [memberSelectedRole, setMemberSelectedRole] = useState('Agent');
    const [memberAddLoading, setMemberAddLoading] = useState(false);
    // Edit an agent's name / phone number
    const [memberEditTarget, setMemberEditTarget] = useState(null);
    const [memberEditName, setMemberEditName] = useState('');
    const [memberEditMobile, setMemberEditMobile] = useState('');
    const [memberEditError, setMemberEditError] = useState('');
    const [memberEditSaving, setMemberEditSaving] = useState(false);
    // View management
    const [currentView, setCurrentView] = useState('vendor'); // 'vendor' or 'customer'
    // Captured once at mount, before the loader clears the URL — so a StrictMode
    // double-invoke of the loader still knows we arrived from the public store.
    const [cameFromStore] = useState(() => new URLSearchParams(window.location.search).get('view') === 'customer');
    // If we arrived from a specific store (?business_id=), open that vendor in the
    // customer dashboard. Captured at mount before the loader clears the URL.
    const [initialCustomerBusinessId] = useState(() => {
        const id = parseInt(new URLSearchParams(window.location.search).get('business_id'), 10);
        return Number.isFinite(id) ? id : null;
    });
    const [roleSelectionDialog, setRoleSelectionDialog] = useState(false);
    // Member ops
    const [memberOpsError, setMemberOpsError] = useState('');
    const [memberOpsSuccess, setMemberOpsSuccess] = useState('');
    const [memberRoleChanging, setMemberRoleChanging] = useState({});
    // Inline business detail editing
    const [bizDetailName, setBizDetailName] = useState('');
    const [bizDetailGst, setBizDetailGst] = useState('');
    const [bizDetailUpi, setBizDetailUpi] = useState('');
    const [bizDetailPhone, setBizDetailPhone] = useState('');
    const [bizDetailAddress1, setBizDetailAddress1] = useState('');
    const [bizDetailCity, setBizDetailCity] = useState('');
    const [bizDetailState, setBizDetailState] = useState('');
    const [bizDetailPin, setBizDetailPin] = useState('');
    const [bizDetailStorePublic, setBizDetailStorePublic] = useState(false);
    const [bizDetailMode, setBizDetailMode] = useState('subscription');
    const [bizDetailLogoUrl, setBizDetailLogoUrl] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoError, setLogoError] = useState('');
    const [bizDetailLoading, setBizDetailLoading] = useState(false);
    const [bizDetailError, setBizDetailError] = useState('');
    const [bizDetailSuccess, setBizDetailSuccess] = useState('');
    // Order Settings (settingsSubTab === 4)
    const [orderSettingsLoading, setOrderSettingsLoading] = useState(false);
    const [orderSettingsSaving, setOrderSettingsSaving] = useState(false);
    const [orderSettingsError, setOrderSettingsError] = useState('');
    const [orderSettingsSuccess, setOrderSettingsSuccess] = useState('');
    const [osNotes, setOsNotes] = useState('');
    const [osDeliveryCharge, setOsDeliveryCharge] = useState('');
    const [osFulfillmentMode, setOsFulfillmentMode] = useState('delivery');
    const [osDefaultOrderStatus, setOsDefaultOrderStatus] = useState('created');
    const [osDefaultPaymentStatus, setOsDefaultPaymentStatus] = useState('due');
    const [osBillingMode, setOsBillingMode] = useState('none');
    // Defaults for orders the vendor/staff create themselves
    const [osVendorOrderStatus, setOsVendorOrderStatus] = useState('created');
    const [osVendorPaymentStatus, setOsVendorPaymentStatus] = useState('due');
    const [osVendorFulfillment, setOsVendorFulfillment] = useState('delivery');
    const [osVendorDeliveryCharge, setOsVendorDeliveryCharge] = useState('');
    const [osVendorPaymentMethod, setOsVendorPaymentMethod] = useState('Cash'); // used when vendor default payment is 'paid_directly'
    const [orderSettingsView, setOrderSettingsView] = useState('online'); // 'online' | 'vendor'
    const [osHours, setOsHours] = useState(null);
    const [osHoursExpanded, setOsHoursExpanded] = useState(false);
    const [osTerms, setOsTerms] = useState('');
    const [orderSettingsData, setOrderSettingsData] = useState(null);
    const [profileOpen, setProfileOpen] = useState(false);
    const [profileName, setProfileName] = useState('');
    const [profileCurrentPin, setProfileCurrentPin] = useState('');
    const [profileNewPin, setProfileNewPin] = useState('');
    const [profileConfirmPin, setProfileConfirmPin] = useState('');
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [profileSuccess, setProfileSuccess] = useState('');
    const [barsVisible, setBarsVisible] = useState(true);
    const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false);
    const [adminMode, setAdminMode] = useState(false);
    const [ordersSubTab, setOrdersSubTab] = useState(0);
    // Pending-notification counts behind the badges: Orders "Alerts" sub-tab,
    // Plans nav (sign-up requests + plan changes), and Dues nav (payment confirmations).
    // One summary call feeds all three; refetched on tab changes so counts stay current.
    const [orderAlertCount, setOrderAlertCount] = useState(0);
    const [planAlertCount, setPlanAlertCount] = useState(0);
    const [duesAlertCount, setDuesAlertCount] = useState(0);
    // Product sharing: another vendor's share waiting here (badge on Settings + banner on Products)
    const [shareAlertCount, setShareAlertCount] = useState(0);
    const [pendingShares, setPendingShares] = useState([]);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [openShare, setOpenShare] = useState(null);   // the share being picked from
    useEffect(() => {
        if (!selectedBusinessId) return;
        notificationAPI.summary(selectedBusinessId)
            .then(res => {
                const d = res.data || {};
                setOrderAlertCount(d.pending_order || 0);
                setPlanAlertCount((d.pending_subscription || 0) + (d.pending_signup || 0));
                setDuesAlertCount(d.pending_bill || 0);
                setShareAlertCount(d.pending_catalogue_share || 0);
            })
            .catch(() => {});
    }, [selectedBusinessId, activeTab, ordersSubTab]);

    const loadPendingShares = useCallback(() => {
        if (!selectedBusinessId) return;
        notificationAPI.list(selectedBusinessId, { type: 'catalogue_share', status: 'pending' })
            .then(res => { setPendingShares(res.data || []); setShareAlertCount((res.data || []).length); })
            .catch(() => {});
    }, [selectedBusinessId]);
    useEffect(() => {
        if (activeTab === 0 && settingsSubTab === 0) loadPendingShares();
    }, [activeTab, settingsSubTab, loadPendingShares]);

    const dismissShare = async (share) => {
        try { await notificationAPI.reject(selectedBusinessId, share.f_id); } catch { /* already gone */ }
        loadPendingShares();
    };

    // Edit business dialog
    const [editBizOpen, setEditBizOpen] = useState(false);
    const [editBizTarget, setEditBizTarget] = useState(null);
    const [editBizData, setEditBizData] = useState({});
    const [editBizLoading, setEditBizLoading] = useState(false);
    const [editBizError, setEditBizError] = useState('');


    useEffect(() => {
        let lastY = window.scrollY;
        const handleScroll = () => {
            const currentY = window.scrollY;
            if (currentY < 50) { setBarsVisible(true); lastY = currentY; return; }
            if (currentY > lastY + 24) {
                setBarsVisible(false);
                lastY = currentY;
                return;
            }
            if (currentY < lastY - 48) {
                setBarsVisible(true);
                lastY = currentY;
                return;
            }
            lastY = currentY;
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        loadUserData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedBusinessId) {
            setSelectedCategoryFilter('');
            setOrdersSubTab(0);
            loadBusinessProducts(selectedBusinessId);
            loadBusinessCustomers(selectedBusinessId);
            loadBusinessMembers(selectedBusinessId);
            loadBusinessAreas(selectedBusinessId);
            businessAPI.getOrderSettings(selectedBusinessId)
                .then(res => setOrderSettingsData(res.data))
                .catch(() => setOrderSettingsData(null));
        }
    }, [selectedBusinessId]); // eslint-disable-line react-hooks/exhaustive-deps -- reload only when the business changes

    const loadBusinessMembers = async (businessId) => {
        try {
            const response = await businessAPI.getBusinessMembers(businessId);
            setBusinessMembers(response.data || []);
        } catch (error) {
            setBusinessMembers([]);
        }
    };

    const loadBusinessAreas = async (businessId) => {
        try {
            const response = await businessAPI.getBusinessAreas(businessId);
            setBusinessAreas(response.data || []);
        } catch {
            setBusinessAreas([]);
        }
    };

    const DEFAULT_HOURS = {
        mon: { open: '09:00', close: '18:00', closed: false },
        tue: { open: '09:00', close: '18:00', closed: false },
        wed: { open: '09:00', close: '18:00', closed: false },
        thu: { open: '09:00', close: '18:00', closed: false },
        fri: { open: '09:00', close: '18:00', closed: false },
        sat: { open: '10:00', close: '14:00', closed: false },
        sun: { open: '10:00', close: '14:00', closed: true },
    };

    // Seed business detail form when tab is opened
    useEffect(() => {
        if (settingsSubTab === 3 && selectedBusiness) {
            setBizDetailName(selectedBusiness.f_name || '');
            setBizDetailGst(selectedBusiness.f_gst || '');
            setBizDetailUpi(selectedBusiness.f_upi_id || '');
            setBizDetailPhone(selectedBusiness.f_phone || '');
            setBizDetailAddress1(selectedBusiness.f_address1 || '');
            setBizDetailCity(selectedBusiness.f_city || '');
            setBizDetailState(selectedBusiness.f_state || '');
            setBizDetailPin(selectedBusiness.f_pincode || '');
            setBizDetailStorePublic(selectedBusiness.f_public_store_enabled ?? false);
            setBizDetailMode(selectedBusiness.f_business_mode || 'subscription');
            setBizDetailLogoUrl(selectedBusiness.f_logo_url || null);
            setLogoError('');
            setBizDetailError('');
            setBizDetailSuccess('');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsSubTab, selectedBusinessId]);

    useEffect(() => {
        if (settingsSubTab === 4 && selectedBusinessId) {
            setBizDetailStorePublic(selectedBusiness?.f_public_store_enabled ?? false);
            setOrderSettingsLoading(true);
            setOrderSettingsError('');
            setOrderSettingsSuccess('');
            setOsHoursExpanded(false);
            businessAPI.getOrderSettings(selectedBusinessId)
                .then(res => {
                    const d = res.data;
                    setOsNotes(d.f_online_store_notes || '');
                    setOsDeliveryCharge(d.f_default_delivery_charge ?? '');
                    setOsFulfillmentMode(d.f_fulfillment_mode || 'delivery');
                    setOsDefaultOrderStatus(d.f_default_order_status || 'created');
                    setOsDefaultPaymentStatus(d.f_default_payment_status || 'due');
                    setOsBillingMode(d.f_billing_mode || 'none');
                    setOsVendorOrderStatus(d.f_vendor_default_order_status || 'created');
                    setOsVendorPaymentStatus(d.f_vendor_default_payment_status || 'due');
                    setOsVendorFulfillment(d.f_vendor_default_fulfillment_type || 'delivery');
                    setOsVendorDeliveryCharge(d.f_vendor_default_delivery_charge ?? '');
                    setOsVendorPaymentMethod(d.f_vendor_default_payment_method || 'Cash');
                    setOsHours(d.f_opening_hours || DEFAULT_HOURS);
                    setOsTerms(d.f_terms_conditions || '');
                    setOrderSettingsData(d);
                })
                .catch(() => {
                    setOsFulfillmentMode('delivery');
                    setOsDefaultOrderStatus('created');
                    setOsDefaultPaymentStatus('due');
                    setOsHours(DEFAULT_HOURS);
                })
                .finally(() => setOrderSettingsLoading(false));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsSubTab, selectedBusinessId]);

    // Keep the default payment status valid when the enabled billing options change
    useEffect(() => {
        if (osDefaultPaymentStatus === 'pay_later' && !osBillingMode.includes('pay_later')) {
            setOsDefaultPaymentStatus('due');
        }
        if (osDefaultPaymentStatus === 'add_to_monthly_bill' && !osBillingMode.includes('monthly')) {
            setOsDefaultPaymentStatus('due');
        }
        if (osVendorPaymentStatus === 'pay_later' && !osBillingMode.includes('pay_later')) {
            setOsVendorPaymentStatus('due');
        }
        if (osVendorPaymentStatus === 'add_to_monthly_bill' && !osBillingMode.includes('monthly')) {
            setOsVendorPaymentStatus('due');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [osBillingMode]);

    const handleLogoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoError('');
        setLogoUploading(true);
        try {
            const compressed = await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    const MAX = 512;
                    let { width, height } = img;
                    if (width > MAX || height > MAX) {
                        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
                        else { width = Math.round(width * MAX / height); height = MAX; }
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = width; canvas.height = height;
                    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Compression failed')), 'image/jpeg', 0.85);
                };
                img.onerror = reject;
                img.src = URL.createObjectURL(file);
            });
            const res = await businessAPI.uploadBusinessLogo(selectedBusinessId, new File([compressed], 'logo.jpg', { type: 'image/jpeg' }));
            setBizDetailLogoUrl(res.data.f_logo_url);
            setBusinesses(prev => prev.map(b => b.f_id === selectedBusinessId ? { ...b, f_logo_url: res.data.f_logo_url } : b));
        } catch {
            setLogoError('Upload failed. Please try again.');
        } finally {
            setLogoUploading(false);
            e.target.value = '';
        }
    };

    const handleLogoRemove = async () => {
        setLogoError('');
        setLogoUploading(true);
        try {
            await businessAPI.deleteBusinessLogo(selectedBusinessId);
            setBizDetailLogoUrl(null);
            setBusinesses(prev => prev.map(b => b.f_id === selectedBusinessId ? { ...b, f_logo_url: null } : b));
        } catch {
            setLogoError('Failed to remove logo.');
        } finally {
            setLogoUploading(false);
        }
    };

    const handleBizDetailSave = async () => {
        if (!bizDetailName.trim()) { setBizDetailError('Business name is required'); return; }
        setBizDetailLoading(true);
        setBizDetailError('');
        setBizDetailSuccess('');
        try {
            const res = await businessAPI.updateBusiness(selectedBusinessId, {
                f_name: bizDetailName.trim(),
                f_gst: bizDetailGst.trim() || null,
                f_upi_id: bizDetailUpi.trim() || null,
                f_phone: bizDetailPhone.trim() || null,
                f_address1: bizDetailAddress1.trim() || undefined,
                f_city: bizDetailCity.trim() || null,
                f_state: bizDetailState.trim() || null,
                f_pincode: bizDetailPin.trim() || null,
                f_public_store_enabled: bizDetailStorePublic,
                f_business_mode: bizDetailMode,
            });
            setBusinesses(prev => prev.map(b => b.f_id === selectedBusinessId ? { ...b, ...res.data } : b));
            setBizDetailSuccess('Business details updated.');
        } catch (err) {
            setBizDetailError(err.response?.data?.detail || 'Failed to update business');
        } finally {
            setBizDetailLoading(false);
        }
    };

    const handleOrderSettingsSave = async () => {
        setOrderSettingsSaving(true);
        setOrderSettingsError('');
        setOrderSettingsSuccess('');
        try {
            const res = await businessAPI.updateOrderSettings(selectedBusinessId, {
                f_online_store_notes:      osNotes.trim() || null,
                f_default_delivery_charge: osDeliveryCharge !== '' ? parseFloat(osDeliveryCharge) : null,
                f_fulfillment_mode:        osFulfillmentMode,
                f_default_order_status:    osDefaultOrderStatus,
                f_default_payment_status:  osDefaultPaymentStatus,
                f_billing_mode:            osBillingMode,
                f_vendor_default_order_status:     osVendorOrderStatus,
                f_vendor_default_payment_status:   osVendorPaymentStatus,
                f_vendor_default_fulfillment_type: osVendorFulfillment,
                f_vendor_default_delivery_charge:  osVendorDeliveryCharge !== '' ? parseFloat(osVendorDeliveryCharge) : null,
                f_vendor_default_payment_method:   osVendorPaymentStatus === 'paid_directly' ? osVendorPaymentMethod : null,
                f_opening_hours:           osHours,
                f_terms_conditions:        osTerms.trim() || null,
            });
            // Refresh the shared settings from the saved response so the New Order dialog
            // picks up the new defaults immediately (not just f_billing_mode).
            if (res?.data) {
                setOrderSettingsData(res.data);
            } else {
                setOrderSettingsData(prev => ({ ...prev, f_billing_mode: osBillingMode }));
            }
            setOrderSettingsSuccess('Order settings saved.');
            handleProductsUpdated();
        } catch (err) {
            setOrderSettingsError(err.response?.data?.detail || 'Failed to save');
        } finally {
            setOrderSettingsSaving(false);
        }
    };

    // --- Inline area CRUD ---
    const handleAreaAdd = async () => {
        if (!newAreaName.trim()) return;
        setAreaOpsLoading(true);
        setAreaOpsError('');
        try {
            const payload = { f_business_id: selectedBusinessId, f_name: newAreaName.trim() };
            if (addAreaPicked) {
                if (addAreaPicked.f_id) {
                    payload.f_community_id = addAreaPicked.f_id;
                } else {
                    const res = await businessAPI.upsertCommunity(addAreaPicked);
                    payload.f_community_id = res.data.community_id;
                }
            }
            if (newAreaDeliveryCharge !== '') payload.f_delivery_charge = parseFloat(newAreaDeliveryCharge);
            await businessAPI.createBusinessArea(payload);
            setNewAreaName('');
            setAddAreaPicked(null);
            setNewAreaDeliveryCharge('');
            setAreaShowAddForm(false);
            await loadBusinessAreas(selectedBusinessId);
            if (newAreaDeliveryCharge !== '') handleProductsUpdated();
        } catch (err) {
            setAreaOpsError(err.response?.data?.detail || 'Failed to add area');
        } finally {
            setAreaOpsLoading(false);
        }
    };

    const handleAreaSaveEdit = async () => {
        if (!areaEditName.trim()) return;
        setAreaOpsLoading(true);
        setAreaOpsError('');
        try {
            const payload = { f_name: areaEditName.trim() };
            if (editAreaPicked) {
                if (editAreaPicked.f_id) {
                    payload.f_community_id = editAreaPicked.f_id;
                } else {
                    const res = await businessAPI.upsertCommunity(editAreaPicked);
                    payload.f_community_id = res.data.community_id;
                }
            } else if (editAreaCleared) {
                payload.f_community_id = null;
            }
            if (editAreaDeliveryCharge !== '') payload.f_delivery_charge = parseFloat(editAreaDeliveryCharge);
            await businessAPI.updateBusinessArea(areaEditingId, payload);
            setAreaEditingId(null);
            setAreaEditName('');
            setEditAreaPicked(null);
            setEditAreaExistingName(null);
            setEditAreaCleared(false);
            setEditAreaDeliveryCharge('');
            await loadBusinessAreas(selectedBusinessId);
            if (editAreaDeliveryCharge !== '') handleProductsUpdated();
        } catch (err) {
            setAreaOpsError(err.response?.data?.detail || 'Failed to update area');
        } finally {
            setAreaOpsLoading(false);
        }
    };

    const handleAreaDelete = async (areaId) => {
        if (!window.confirm('Delete this area? Customers assigned to it will be unaffected.')) return;
        setAreaOpsLoading(true);
        setAreaOpsError('');
        try {
            await businessAPI.deleteBusinessArea(areaId);
            await loadBusinessAreas(selectedBusinessId);
        } catch (err) {
            setAreaOpsError(err.response?.data?.detail || 'Cannot delete: customers are assigned to this area');
        } finally {
            setAreaOpsLoading(false);
        }
    };

    // Customers belonging to no area. They are delivered last, and a vendor with a single
    // locality never creates areas at all — so this bucket still needs its own round order.
    const noAreaCustomers = useMemo(
        () => businessCustomers.filter(c => !c.f_area_id && !c.f_is_archived),
        [businessCustomers]
    );

    // Customers in the area whose delivery order is currently open.
    const routeOrderCustomers = useMemo(() => {
        if (!routeOrderArea) return [];
        return businessCustomers.filter(c =>
            !c.f_is_archived && (c.f_area_id ?? null) === routeOrderArea.id);
    }, [businessCustomers, routeOrderArea]);

    // Sensors shared by the area list. Mouse: drag after a small move. Touch: press-and-hold
    // briefly so a plain swipe still scrolls the page.
    const areaDragSensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { distance: 8 } }),
    );

    const handleAreaDragEnd = async ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = businessAreas.findIndex(a => a.f_id === active.id);
        const newIndex = businessAreas.findIndex(a => a.f_id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const previous = businessAreas;
        const moved = arrayMove(businessAreas, oldIndex, newIndex);
        setBusinessAreas(moved);   // optimistic
        setAreaOpsError('');
        try {
            await businessAPI.reorderAreas(selectedBusinessId, moved.map(a => a.f_id));
        } catch (err) {
            setBusinessAreas(previous);
            setAreaOpsError(err.response?.data?.detail || 'Could not save the area order. Please try again.');
        }
    };

    // Put customers into an area or agent (or take them out, with groupId null). One update
    // per customer, in turn, so each lands in the round in the order chosen. Local state follows
    // every success; failures are counted rather than stopping the rest.
    // Moving area gives the customer a fresh position at the end of the new round, so the rank
    // comes back from the server.
    const assignCustomersTo = async (field, groupId, ids, setErr) => {
        let failed = 0;
        let lastError = '';
        for (const id of ids) {
            try {
                const res = await businessAPI.updateCustomer(id, { [field]: groupId });
                setBusinessCustomers(prev => prev.map(c => (c.f_id === id
                    ? { ...c, [field]: groupId,
                        ...(field === 'f_area_id' ? { f_sort_order: res?.data?.f_sort_order ?? c.f_sort_order } : {}) }
                    : c)));
            } catch (err) {
                failed++;
                lastError = err.response?.data?.detail || 'Failed to update customer';
            }
        }
        if (failed) setErr(lastError);
        return { failed };
    };

    // --- Inline member/agent CRUD ---
    // An agent does NOT need a Vendora account to be added. The lookup below is advisory
    // only — it tells the vendor whose number this is, so a mistyped digit is visible before
    // they save. It never blocks the add.
    const handleMemberLookup = async () => {
        const mobile = memberMobileInput.trim();
        if (!mobile) return;
        setMemberLookupLoading(true);
        setMemberLookupResult(null);
        setMemberLookupError('');
        try {
            const res = await businessAPI.lookupUserByMobile(mobile);
            setMemberLookupResult(res.data);   // { found, f_name?, f_mobile }
        } catch (e) {
            setMemberLookupError(e.response?.data?.detail || 'Could not check this number');
        } finally {
            setMemberLookupLoading(false);
        }
    };

    const handleMemberAdd = async () => {
        const mobile = memberMobileInput.trim();
        if (!mobile) return;
        const name = memberNameInput.trim();
        setMemberAddLoading(true);
        setMemberOpsError('');
        setMemberOpsSuccess('');
        try {
            await businessAPI.addBusinessMember(selectedBusinessId, {
                mobile,
                name: name || null,
                role: memberSelectedRole,
            });
            setMemberOpsSuccess(`${name || mobile} added as ${memberSelectedRole}.`);
            setMemberShowAddForm(false);
            setMemberMobileInput('');
            setMemberNameInput('');
            setMemberLookupResult(null);
            setMemberSelectedRole('Agent');
            await loadBusinessMembers(selectedBusinessId);
            await loadBusinessCustomers(selectedBusinessId);
        } catch (e) {
            setMemberOpsError(e.response?.data?.detail || 'Failed to add agent');
        } finally {
            setMemberAddLoading(false);
        }
    };

    // Editing name / phone number. Changing the number re-points who this agent is: the
    // account currently linked loses access to this business and the new number's account,
    // if it has one, gains it. The agent's customers and orders stay on the row either way.
    const handleMemberEditSave = async () => {
        if (!memberEditTarget) return;
        const mobile = (memberEditMobile || '').trim();
        const name = (memberEditName || '').trim();
        if (!mobile) { setMemberEditError('Phone number is needed.'); return; }
        setMemberEditSaving(true);
        setMemberEditError('');
        try {
            await businessAPI.updateBusinessMember(selectedBusinessId, memberEditTarget.f_id, {
                name: name || null,
                mobile,
            });
            setMemberEditTarget(null);
            setMemberOpsSuccess('Agent updated.');
            await loadBusinessMembers(selectedBusinessId);
        } catch (e) {
            setMemberEditError(e.response?.data?.detail || 'Could not save');
        } finally {
            setMemberEditSaving(false);
        }
    };

    // Opens the remove box. An agent with customers can't just go: the vendor picks who gets
    // them, or no agent — otherwise they drop out of every agent filter.
    // Position of a customer's area in the route (Settings → Areas order), for "Sort by Area".
    const areaRankOf = (c) => {
        const i = sortAreasByRoute(businessAreas).findIndex(a => a.f_id === c.f_area_id);
        return i < 0 ? null : i;
    };

    // Agent name for a member id; the owner is marked, since a vendor can be their own agent.
    const agentNameOf = (id) => {
        if (!id) return null;
        const m = businessMembers.find(x => x.f_id === id);
        if (!m) return 'Other agent';
        return m.f_role === 'Owner' ? `${m.f_name || m.f_mobile} (owner)` : (m.f_name || m.f_mobile);
    };

    const handleMemberRemove = (member) => {
        setMemberOpsError('');
        setMemberOpsSuccess('');
        const count = businessCustomers.filter(c => c.f_agent_id === member.f_id && !c.f_is_archived).length;
        setMemberRemove({ member, count, moveTo: null });
    };

    const confirmMemberRemove = async () => {
        const { member, count, moveTo } = memberRemove;
        setMemberRemoving(true);
        try {
            const res = await businessAPI.removeBusinessMember(selectedBusinessId, member.f_id, count > 0 ? moveTo : null);
            const moved = res.data?.moved || 0;
            const to = moveTo === 'none' ? 'no agent' : businessMembers.find(m => m.f_id === moveTo)?.f_name;
            setMemberOpsSuccess(moved ? `${member.f_name || member.f_mobile} removed. ${moved} customers moved to ${to}.` : 'Member removed.');
            setMemberRemove(null);
            await loadBusinessMembers(selectedBusinessId);
            if (moved) loadBusinessCustomers(selectedBusinessId);
        } catch (e) {
            setMemberOpsError(e.response?.data?.detail || 'Failed to remove member');
            setMemberRemove(null);
        } finally {
            setMemberRemoving(false);
        }
    };

    const handleMemberRoleChange = async (member, newRole) => {
        if (newRole === member.f_role) return;
        setMemberRoleChanging(prev => ({ ...prev, [member.f_id]: true }));
        setMemberOpsError('');
        setMemberOpsSuccess('');
        try {
            await businessAPI.updateBusinessMember(selectedBusinessId, member.f_id, { role: newRole });
            setMemberOpsSuccess('Role updated.');
            await loadBusinessMembers(selectedBusinessId);
        } catch (e) {
            setMemberOpsError(e.response?.data?.detail || 'Failed to update role');
        } finally {
            setMemberRoleChanging(prev => ({ ...prev, [member.f_id]: false }));
        }
    };

    const loadProductCategoryOrder = (businessId) => {
        if (!businessId) return;
        businessAPI.getCategoryOrder(businessId)
            .then(res => setProductCategoryOrder(res.data?.ordered_names || []))
            .catch(() => setProductCategoryOrder([]));
    };

    const loadBusinessProducts = async (businessId) => {
        try {
            // include_plans: plans ("Sunday Curd") are managed in this same list, nested under
            // the product they sell, so a vendor sees everything they sell in one place.
            const response = await businessAPI.getBusinessProducts(businessId, true);
            setBusinessProducts(response.data || []);
            loadProductCategoryOrder(businessId);
        } catch (error) {
            console.error('Failed to load business products:', error);
            setBusinessProducts([]);
        }
    };

    const loadBusinessCustomers = async (businessId) => {
        try {
            const response = await businessAPI.getCustomers(businessId, false, null);
            setBusinessCustomers(response.data || []);
        } catch (error) {
            console.error('Failed to load business customers:', error);
            setBusinessCustomers([]);
        }
    };

    const handleProductsUpdated = () => {
        if (selectedBusinessId) {
            loadBusinessProducts(selectedBusinessId);
        }
    };

    /**
     * Customers with at least one plan still running, from a subscription-log list.
     *
     * The log is append-only and the API returns it newest-first, so the first row seen for a
     * (customer, product) pair is that plan's current state. Two things this gets right that the
     * `Active` action filter does not: a customer who started and later stopped is no longer
     * counted (their Start row lives on forever), and the result is customers rather than rows.
     * Keyed per product as well as per customer, so a live Milk plan still counts for someone
     * who once ended Curd. Mirrors _active_customer_ids_for_product() in business_routes.py.
     *
     * Archived customers are dropped (businessCustomers holds only current ones), as the server
     * does: otherwise "2 already on this plan" opened onto an empty list, and the delete check
     * counted customers the vendor had no way to find.
     */
    const activeCustomerIdsFromLogs = (logs) => {
        const current = new Set(businessCustomers.map(c => c.f_id));
        const seen = new Set();
        const active = new Set();
        for (const log of logs || []) {
            const key = `${log.f_business_customer_id}_${log.f_business_product_id}`;
            if (seen.has(key)) continue;
            seen.add(key);
            if (log.f_action !== 'End' && current.has(log.f_business_customer_id)) {
                active.add(log.f_business_customer_id);
            }
        }
        return active;
    };

    const handleArchiveProductClick = async (product) => {
        setArchiveProductChecking(true);
        setArchiveProductTarget(product);
        setArchiveProductActivePlans(0);
        setArchiveProductError('');
        setArchiveOpenMonths([]);
        // Which bill months are still open. Not just "this month": last month stays open until
        // the vendor closes it, and a deleted product drops off any bill made again for it.
        businessAPI.listInvoicePeriods(selectedBusinessId).then(res => {
            const now = new Date();
            const thisKey = now.getFullYear() * 12 + now.getMonth();
            const periods = (res.data || []).filter(p => !p.f_is_deleted);
            const keyOf = p => p.f_period_year * 12 + (p.f_period_month - 1);
            // A month is open unless its bill month has been closed — including months whose bill
            // month hasn't been made yet (August open, September not made: both still billable).
            const closed = new Set(periods.filter(p => p.f_is_locked).map(keyOf));
            const firstOpen = Math.min(
                thisKey,
                ...periods.filter(p => !p.f_is_locked).map(keyOf),
                ...(closed.size ? [Math.max(...closed) + 1] : []),
            );
            const open = [];
            for (let k = firstOpen; k <= thisKey; k++) if (!closed.has(k)) open.push(k);
            setArchiveOpenMonths([...new Set(open)].sort((a, b) => a - b).map(k =>
                new Date(Math.floor(k / 12), k % 12, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })));
        }).catch(() => {
            setArchiveOpenMonths([new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })]);
        });
        try {
            // No action filter — we need every row so the latest one per customer wins, exactly
            // as the server's delete guard decides it. Otherwise the dialog and the server disagree.
            const res = await businessAPI.getCustomerSubscriptions(selectedBusinessId, null, product.f_id);
            const holders = activeCustomerIdsFromLogs(res.data);
            setArchiveProductActivePlans(holders.size);
            setArchiveProductHolderIds([...holders]);
        } catch {
            setArchiveProductActivePlans(0);
            setArchiveProductHolderIds([]);
        } finally {
            setArchiveProductChecking(false);
        }
    };

    const handleArchiveProductConfirm = async () => {
        if (!archiveProductTarget) return;
        const target = archiveProductTarget;
        // Plans (set-day deliveries under a product) and products share this confirm box.
        const isPlan = !!target.f_parent_business_product_id;
        setArchiveProductLoading(true);
        setArchiveProductError('');
        try {
            if (isPlan) {
                await businessAPI.deletePlan(selectedBusinessId, target.f_id);
                handleProductsUpdated();
            } else {
                await businessAPI.deleteBusinessProduct(target.f_id);
                setBusinessProducts(prev => prev.filter(p => p.f_id !== target.f_id));
            }
            setArchiveProductTarget(null);
            setDeleteNotice(isPlan
                ? `Plan "${target.f_name}" deleted.`
                : `"${target.f_name}" deleted. To bring it back: Settings → Deleted.`);
        } catch (e) {
            // Stay open and say why — the server refuses while customers are still on it.
            setArchiveProductError(e.response?.data?.detail || 'Could not delete. Please try again.');
        } finally {
            setArchiveProductLoading(false);
        }
    };

    // Customers are still on it: end all their plans today, and optionally delete in the same step.
    const handleEndAllPlans = async (deleteToo) => {
        const target = archiveProductTarget;
        if (!target) return;
        const isPlan = !!target.f_parent_business_product_id;
        setArchiveEndingMode(deleteToo ? 'end_delete' : 'end');
        setArchiveProductError('');
        try {
            const res = await businessAPI.endAllPlansOnProduct(target.f_id, deleteToo);
            const n = res.data?.ended ?? archiveProductActivePlans;
            const plans = `${n} plan${n !== 1 ? 's' : ''}`;
            setArchiveProductTarget(null);
            handleProductsUpdated();
            setDeleteNotice(deleteToo
                ? `Ended ${plans} and deleted "${target.f_name}".${isPlan ? '' : ' To bring it back: Settings → Deleted.'}`
                : `Ended ${plans} on "${target.f_name}". Delete it after the open bills are closed.`);
        } catch (e) {
            setArchiveProductError(e.response?.data?.detail || 'Could not end the plans. Please try again.');
        } finally {
            setArchiveEndingMode(null);
        }
    };

    const handleOpenPlanDialog = async (product) => {
        const today = new Date().toISOString().split('T')[0];
        setPlanProductId(product.f_id);
        setPlanProductSubs([]);
        setPlanSelectedIds([]);
        setPlanCustomerQty({});
        setPlanQuantity(1);
        setPlanStartDate('');
        setPlanProductSearch('');
        setPlanProductPage(50);
        setPlanAdding(false);
        setPlanAddResult(null);
        setPlanFilterBy('no_plans');
        setPlanAllSubCustIds(new Set());
        setPlanAssignedExpanded(false);
        setPlanAssignedPage(50);
        setPlanProductLoading(true);
        try {
            // Unfiltered, so the latest row per customer decides. With the 'Active' filter a
            // customer who had stopped still looked assigned, and could never be added back.
            const [productSubsRes, allSubsRes] = await Promise.all([
                businessAPI.getCustomerSubscriptions(selectedBusinessId, null, product.f_id),
                businessAPI.getCustomerSubscriptions(selectedBusinessId, null, null),
            ]);
            setPlanProductSubs([...activeCustomerIdsFromLogs(productSubsRes.data)]);
            setPlanAllSubCustIds(activeCustomerIdsFromLogs(allSubsRes.data));
        } catch {
            setPlanProductSubs([]);
            setPlanAllSubCustIds(new Set());
        } finally {
            setPlanProductLoading(false);
        }
    };

    const handleBulkAddPlans = async () => {
        if (!planSelectedIds.length || !planProductId || !planStartDate) return;
        setPlanAdding(true);
        setPlanAddResult(null);
        let added = 0; let failed = 0;
        for (const custId of planSelectedIds) {
            try {
                await businessAPI.createCustomerSubscription({
                    f_business_id: selectedBusinessId,
                    f_business_customer_id: custId,
                    f_business_product_id: planProductId,
                    f_quantity: planCustomerQty[custId] ?? planQuantity,
                    f_effective_from_date: planStartDate,
                    f_updated_by: user?.f_id,
                    f_action: 'Start',
                });
                added++;
                setPlanProductSubs(prev => [...prev, custId]);
                setPlanSelectedIds(prev => prev.filter(id => id !== custId));
            } catch {
                failed++;
            }
        }
        setPlanAdding(false);
        setPlanAddResult({ added, failed });
        // Refresh the customer counts on the product cards
        if (added) loadBusinessProducts(selectedBusinessId);
    };


    const handleCustomersUpdated = () => {
        if (selectedBusinessId) {
            loadBusinessCustomers(selectedBusinessId);
        }
    };

    const handleOpenEditBiz = (b) => {
        setEditBizTarget(b);
        setEditBizData({
            name: b.f_name || '',
            gst: b.f_gst || '',
            address1: b.f_address1 || '',
            address2: b.f_address2 || '',
            city: b.f_city || '',
            state: b.f_state || '',
            pincode: b.f_pincode || '',
            phone: b.f_phone || '',
            email: b.f_email || '',
            upi_id: b.f_upi_id || '',
            business_mode: b.f_business_mode || 'subscription',
            public_store_enabled: b.f_public_store_enabled || false,
        });
        setEditBizError('');
        setEditBizOpen(true);
    };

    const handleSaveEditBiz = async () => {
        if (!editBizData.name?.trim()) { setEditBizError('Business name is required'); return; }
        setEditBizLoading(true);
        setEditBizError('');
        try {
            const payload = {
                f_name: editBizData.name.trim(),
                f_gst: editBizData.gst || null,
                f_address1: editBizData.address1 || null,
                f_address2: editBizData.address2 || null,
                f_city: editBizData.city || null,
                f_state: editBizData.state || null,
                f_pincode: editBizData.pincode || null,
                f_phone: editBizData.phone || null,
                f_email: editBizData.email || null,
                f_upi_id: editBizData.upi_id || null,
                f_business_mode: editBizData.business_mode || 'subscription',
                f_public_store_enabled: editBizData.public_store_enabled || false,
            };
            const res = await businessAPI.updateBusiness(editBizTarget.f_id, payload);
            setBusinesses(prev => prev.map(b => b.f_id === editBizTarget.f_id ? { ...b, ...res.data } : b));
            setEditBizOpen(false);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setEditBizError(typeof detail === 'string' ? detail : 'Failed to update business');
        } finally {
            setEditBizLoading(false);
        }
    };

    const loadUserData = async () => {
        try {
            const [userResponse, businessesResponse, categoriesResponse] = await Promise.all([
                authAPI.getCurrentUser(),
                businessAPI.listBusinesses(),
                businessAPI.listProductCategories(),
            ]);

            // Debug: log server response shape when diagnosing missing fields
            console.debug('getCurrentUser response:', userResponse.data);

            // Some responses may include `user_id`/`mobile` (from AuthResponse)
            // while others return the user model with `f_id`/`f_mobile`.
            // Use localStorage as a final fallback so UI shows values immediately after login.
            const serverUser = userResponse.data || {};
            const fallbackId = localStorage.getItem('user_id');
            const fallbackMobile = localStorage.getItem('mobile');

            const normalizedUser = {
                ...serverUser,
                f_id: serverUser.f_id ?? serverUser.user_id ?? (fallbackId ? Number(fallbackId) : undefined),
                f_mobile: serverUser.f_mobile ?? serverUser.mobile ?? fallbackMobile ?? undefined,
                f_name: serverUser.f_name ?? serverUser.name ?? localStorage.getItem('user_name') ?? null,
                f_is_active: serverUser.f_is_active ?? serverUser.is_active ?? true,
                f_inserted_at: serverUser.f_inserted_at ?? serverUser.inserted_at ?? serverUser.created_at ?? null,
            };

            setUser(normalizedUser);
            setAllCategories(categoriesResponse.data || []);

            // Apply user's saved language preference (cross-device sync)
            const serverLang = serverUser.f_preferred_language;
            if (serverLang && ['en', 'te', 'hi'].includes(serverLang)) {
                import('../i18n').then(({ default: i18n }) => {
                    if (i18n.language !== serverLang) i18n.changeLanguage(serverLang);
                });
            }
            
            // Ensure businesses is always an array
            const businessData = businessesResponse.data;
            if (Array.isArray(businessData)) {
                setBusinesses(businessData);
                // Restore last session for this user; wipe it if a different user.
                const session = loadSession();
                const sameUser = session.userId === normalizedUser.f_id;
                if (!sameUser) { clearSession(); setActiveTab(0); }
                const savedBiz = sameUser && businessData.find(b => b.f_id === session.businessId);
                // Auto-select saved business (if it still exists), else the first.
                if (businessData.length > 0 && !selectedBusinessId) {
                    setSelectedBusinessId(savedBiz ? savedBiz.f_id : businessData[0].f_id);
                }
                // If redirected from public store, always go to customer view
                if (cameFromStore) {
                    setCurrentView('customer');
                    window.history.replaceState({}, '', window.location.pathname);
                } else if (businessData.length > 0) {
                    // Resume the last view for this user; default to vendor.
                    setCurrentView(sameUser && session.view === 'customer' ? 'customer' : 'vendor');
                } else {
                    // No businesses, show role selection
                    setRoleSelectionDialog(true);
                }
            } else {
                setBusinesses([]);
                if (cameFromStore) {
                    setCurrentView('customer');
                    window.history.replaceState({}, '', window.location.pathname);
                } else {
                    setRoleSelectionDialog(true);
                }
            }
        } catch (error) {
            console.error('Failed to load user data:', error);
            setBusinesses([]);
        } finally {
            setLoading(false);
        }
    };

    // WhatsApp support, with a message saying who is asking so support knows the business
    // without asking back. Used by the top-bar Help menu and the profile menu.
    const openSupportWhatsApp = () => {
        const who = [selectedBusiness?.f_name, user?.f_mobile].filter(Boolean).join(', ');
        window.open(supportWhatsAppHref(`Hi, I need help with Vendora88${who ? ` (${who})` : ''}.`), '_blank');
    };

    const handleLogout = async () => {
        try {
            await authAPI.logout();
            localStorage.removeItem('access_token');
            localStorage.removeItem('user_id');
            localStorage.removeItem('mobile');
            clearSession();
            onLogout();
        } catch (error) {
            console.error('Logout failed:', error);
            // Still logout locally even if API call fails
            localStorage.removeItem('access_token');
            localStorage.removeItem('user_id');
            localStorage.removeItem('mobile');
            clearSession();
            onLogout();
        }
    };

    const handleBusinessCreated = (businessData) => {
        setShowBusinessForm(false);
        loadUserData(); // Reload to get updated business list
        if (businessData?.f_id) {
            setSelectedBusinessId(businessData.f_id);
        }
    };

    // Persist the current view/business/tab so reopening resumes here.
    useEffect(() => {
        if (!user?.f_id) return;
        saveSession({ userId: user.f_id, view: currentView, businessId: selectedBusinessId, vendorTab: activeTab });
    }, [user, currentView, selectedBusinessId, activeTab]);

    // Clamp the active tab to one that's actually visible for the selected
    // business (its mode may not offer Orders/Billing/Dues/Subscriptions).
    useEffect(() => {
        const biz = businesses.find(b => b.f_id === selectedBusinessId);
        if (!biz) return;
        const mode = biz.f_business_mode || 'subscription';
        const sub = mode === 'subscription' || mode === 'both';
        const ord = mode === 'orders' || mode === 'both';
        const billing = sub || (orderSettingsData?.f_billing_mode || '').includes('monthly');
        const dues = sub || (orderSettingsData?.f_billing_mode || 'none') !== 'none';
        const visible = (idx) => {
            if (idx === 2) return ord;
            if (idx === 3 || idx === 6) return sub;
            if (idx === 4) return billing;
            if (idx === 5) return dues;
            if (idx === 7) return false;
            return true; // 0 (Settings), 1 (Customers) always available
        };
        if (!visible(activeTab)) setActiveTab(0);
    }, [selectedBusinessId, businesses, orderSettingsData, activeTab]);

    const selectedBusiness = businesses.find(b => b.f_id === selectedBusinessId);
    // Settings → Deleted is for the owner and Admins — the server refuses restores from anyone else.
    const canRestoreDeleted = !!selectedBusiness && (
        selectedBusiness.f_created_by === user?.f_id
        || businessMembers.some(m => m.f_user_id === user?.f_id && m.f_role === 'Admin' && m.f_is_active)
    );
    const bizMode = selectedBusiness?.f_business_mode || 'subscription';
    // The vendor's categories in their Sort & Categories order, then any others A–Z.
    const orderedCategoryOptions = (() => {
        const all = [...new Set(businessProducts.flatMap(p => productCategoryList(p)))];
        return [
            ...productCategoryOrder.filter(c => all.includes(c)),
            ...all.filter(c => !productCategoryOrder.includes(c))
                .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })),
        ];
    })();
    const showSubscription = bizMode === 'subscription' || bizMode === 'both';
    const showOrders = bizMode === 'orders' || bizMode === 'both';
    const showBilling = showSubscription || (orderSettingsData?.f_billing_mode || '').includes('monthly');
    // Dues page is hidden only for non-subscription businesses with no credit options enabled
    const showDuesTab = showSubscription || (orderSettingsData?.f_billing_mode || 'none') !== 'none';

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
        {/* Vendors only — the installed app is a vendor tool. Customers reach
            Vendora through a shared store link and have no use for the icon. */}
        {!adminMode && currentView === 'vendor' && <InstallPromptBanner />}
        {/* The phone's status bar (clock, battery) is see-through, and the top bar below slides
            away on scroll — taking its notch padding with it — so lists used to scroll visibly
            under the clock. This strip stays put behind the status bar, in the top bar's colour.
            Dialogs (z-index 1300) still cover it; they keep the notch clear themselves. */}
        {!adminMode && currentView === 'vendor' && (
            <Box aria-hidden sx={{
                position: 'fixed', top: 0, left: 0, right: 0,
                height: 'env(safe-area-inset-top, 0px)',
                bgcolor: (t) => (t.palette.mode === 'light' ? t.palette.grey[100] : t.palette.grey[900]),
                zIndex: (t) => t.zIndex.appBar + 1,
                pointerEvents: 'none',
            }} />
        )}
        {/* Slim top AppBar */}
        {!adminMode && currentView === 'vendor' && (
        <AppBar position="fixed" color="default" elevation={1} sx={{
            top: 0,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            transform: barsVisible ? 'translateY(0)' : 'translateY(-100%)',
            transition: 'transform 0.25s ease',
        }}>
            <Toolbar variant="dense" sx={{ minHeight: 48, maxWidth: 720, width: '100%', mx: 'auto' }}>
                <Button
                    onClick={(e) => setBizAnchorEl(e.currentTarget)}
                    endIcon={<ArrowDropDownIcon />}
                    sx={{ textTransform: 'none', maxWidth: 220 }}
                    color="inherit"
                    size="small"
                >
                    <Typography noWrap variant="body2" fontWeight="bold">
                        {selectedBusiness ? selectedBusiness.f_name : 'Select Business'}
                    </Typography>
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {/* Help: support is one tap from every screen. A word, not just an icon — an
                    unfamiliar icon is the one thing nobody taps. */}
                {/* Tonal, like the app's secondary card buttons: a wash of the brand blue, no border.
                    A thin outline read as a stray form button in an otherwise clean bar. */}
                <Button
                    onClick={(e) => setHelpAnchorEl(e.currentTarget)}
                    startIcon={<HelpOutline sx={{ fontSize: '1.1rem !important' }} />}
                    size="small" disableElevation
                    sx={(theme) => ({
                        textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', letterSpacing: 0.2,
                        borderRadius: 999, px: 1.5, py: 0.5, minHeight: 32, flexShrink: 0,
                        color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.3 : 0.16) },
                        '& .MuiButton-startIcon': { mr: 0.5 },
                    })}
                >
                    Help
                </Button>
                {user?.is_super_admin && (
                    <IconButton onClick={() => setAdminMode(true)} size="small" color="primary">
                        <AdminPanelSettingsIcon />
                    </IconButton>
                )}
                <IconButton
                    onClick={(e) => setProfileAnchorEl(e.currentTarget)}
                    size="small"
                    sx={{ ml: 0.5 }}
                >
                    <AccountCircle />
                </IconButton>
            </Toolbar>
        </AppBar>
        )}

        {/* Business Selector Menu */}
        <Menu
            anchorEl={bizAnchorEl}
            open={Boolean(bizAnchorEl)}
            onClose={() => setBizAnchorEl(null)}
            PaperProps={{ sx: { minWidth: 260 } }}
        >
            <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 1 }}>
                    My Businesses
                </Typography>
            </Box>
            {businesses.map((b) => {
                const isOwner = b.f_created_by === user?.f_id;
                return (
                    <MenuItem
                        key={b.f_id}
                        selected={b.f_id === selectedBusinessId}
                        onClick={() => { setSelectedBusinessId(b.f_id); setCurrentView('vendor'); setBizAnchorEl(null); }}
                        sx={{ pr: 1 }}
                    >
                        <BusinessIcon sx={{ mr: 1.5, fontSize: 18, color: b.f_id === selectedBusinessId ? 'primary.main' : 'text.secondary', flexShrink: 0 }} />
                        <Typography noWrap sx={{ flex: 1, fontWeight: b.f_id === selectedBusinessId ? 'bold' : 'normal' }}>
                            {b.f_name}
                        </Typography>
                        {/* Only the owner gets an action here. Staff used to see a "Leave
                            business" button, but leaving is not theirs to do — the vendor
                            removes an agent from Settings > Agents, which also keeps that
                            agent's customers and orders attached to them. The button also
                            never worked: the endpoint behind it is owner/admin only, so an
                            agent tapping it got a 403. A super admin who needs to leave a
                            business they joined does it from the super admin dashboard. */}
                        {isOwner && (
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleOpenEditBiz(b); setBizAnchorEl(null); }}
                                title="Edit Business"
                                sx={{ ml: 0.5 }}
                            >
                                <ModeEditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </MenuItem>
                );
            })}
            <Divider />
            <MenuItem onClick={() => { setCurrentView('customer'); setBizAnchorEl(null); }}>
                <People sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
                <Typography color="text.secondary">Customer View</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setShowBusinessForm(true); setBizAnchorEl(null); }}>
                <Add sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
                <Typography color="text.secondary">{t('drawer.addBusiness')}</Typography>
            </MenuItem>
        </Menu>

        {/* Help card — Call or WhatsApp support. A small card rather than a bare menu list:
            who you will reach, the number itself, and two clear buttons. */}
        <Popover
            open={Boolean(helpAnchorEl)}
            anchorEl={helpAnchorEl}
            onClose={() => setHelpAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 1, width: 300, maxWidth: 'calc(100vw - 24px)', borderRadius: 3, overflow: 'hidden' } }}
        >
            <Box sx={(theme) => ({
                position: 'relative',
                px: 2.5, pt: 2.5, pb: 2, textAlign: 'center',
                background: `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.14)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            })}>
                {/* Tapping outside also closes it, but on a phone that is not obvious */}
                <IconButton onClick={() => setHelpAnchorEl(null)} aria-label="Close"
                    sx={{ position: 'absolute', top: 6, right: 6, color: 'text.secondary' }}>
                    <Close fontSize="small" />
                </IconButton>
                <Box sx={(theme) => ({
                    width: 48, height: 48, borderRadius: '50%', mx: 'auto', mb: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'primary.main', color: '#fff',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
                })}>
                    <HelpOutline />
                </Box>
                <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>Need help?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    Call or WhatsApp the Vendora88 team
                </Typography>
                <Typography component="a" href={supportCallHref} onClick={() => setHelpAnchorEl(null)}
                    sx={{ display: 'inline-block', mt: 1.25, fontSize: '1.4rem', fontWeight: 800, letterSpacing: 0.5,
                          color: 'primary.main', textDecoration: 'none' }}>
                    {SUPPORT_PHONE_DISPLAY}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 1.5 }}>
                <Button fullWidth component="a" href={supportCallHref} onClick={() => setHelpAnchorEl(null)}
                    variant="contained" disableElevation startIcon={<Phone />}
                    sx={{ minHeight: TOUCH.min, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                    Call
                </Button>
                <Button fullWidth onClick={() => { openSupportWhatsApp(); setHelpAnchorEl(null); }}
                    variant="contained" disableElevation startIcon={<WhatsAppIcon />}
                    sx={{ minHeight: TOUCH.min, borderRadius: 2, textTransform: 'none', fontWeight: 700,
                          bgcolor: WHATSAPP.main, '&:hover': { bgcolor: WHATSAPP.hover } }}>
                    WhatsApp
                </Button>
            </Box>
        </Popover>

        {/* Profile / App Settings Menu */}
        <Menu
            anchorEl={profileAnchorEl}
            open={Boolean(profileAnchorEl)}
            onClose={() => setProfileAnchorEl(null)}
            PaperProps={{ sx: { minWidth: 230 } }}
        >
            <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold">{user?.f_name || user?.f_mobile}</Typography>
                {user?.f_name && <Typography variant="caption" color="text.secondary">{user?.f_mobile}</Typography>}
            </Box>
            <Divider />
            <MenuItem onClick={() => {
                setProfileName(user?.f_name || '');
                setProfileCurrentPin('');
                setProfileNewPin('');
                setProfileConfirmPin('');
                setProfileError('');
                setProfileSuccess('');
                setProfileOpen(true);
                setProfileAnchorEl(null);
            }}>
                <AccountCircle sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
                {t('drawer.editProfile')}
            </MenuItem>
            <MenuItem onClick={() => { setCurrentView('customer'); setProfileAnchorEl(null); }}>
                <People sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
                Switch to Customer View
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { onToggleTheme(); setProfileAnchorEl(null); }}>
                {themeMode === 'dark'
                    ? <LightModeIcon sx={{ mr: 1.5, fontSize: 18, color: 'warning.main' }} />
                    : <DarkModeIcon sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
                }
                {themeMode === 'dark' ? t('drawer.lightMode') : t('drawer.darkMode')}
            </MenuItem>
            <MenuItem onClick={() => { i18n.changeLanguage('en'); setProfileAnchorEl(null); }} selected={i18n.language === 'en'}>
                <TranslateIcon sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
                English
            </MenuItem>
            <MenuItem onClick={() => { i18n.changeLanguage('hi'); setProfileAnchorEl(null); }} selected={i18n.language === 'hi'}>
                <TranslateIcon sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
                हिंदी
            </MenuItem>
            <MenuItem onClick={() => { i18n.changeLanguage('te'); setProfileAnchorEl(null); }} selected={i18n.language === 'te'}>
                <TranslateIcon sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
                తెలుగు
            </MenuItem>
            <MenuItem onClick={() => {
                const text = `Hey! I use *Vendora88* to run my business — it handles orders, billing, customer dues, and monthly subscriptions all in one place. It's really simple to use!\n\nCheck it out: https://vendora88.com\n\nWorks great for:\n🍽️ Restaurants & food orders\n👕 Ironing & laundry services\n🥛 Milk & water delivery\n📰 Newspaper delivery\n📡 Cable & internet operators\n...and any business that needs daily orders or monthly billing.\n\nTry it free!`;
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                setProfileAnchorEl(null);
            }}>
                <WhatsAppIcon sx={{ mr: 1.5, fontSize: 18, color: '#25D366' }} />
                {t('drawer.shareWhatsApp')}
            </MenuItem>
            <Divider />
            {/* Help & support — the vendor's line to us once they are signed in */}
            <Typography variant="caption" color="text.secondary" fontWeight={700}
                sx={{ display: 'block', px: 2, pt: 0.5, pb: 0.25, letterSpacing: 0.4 }}>
                HELP &amp; SUPPORT · {SUPPORT_PHONE_DISPLAY}
            </Typography>
            <MenuItem component="a" href={supportCallHref} onClick={() => setProfileAnchorEl(null)}>
                <Phone sx={{ mr: 1.5, fontSize: 18, color: 'primary.main' }} />
                Call support
            </MenuItem>
            <MenuItem onClick={() => { openSupportWhatsApp(); setProfileAnchorEl(null); }}>
                <WhatsAppIcon sx={{ mr: 1.5, fontSize: 18, color: '#25D366' }} />
                WhatsApp support
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleLogout(); setProfileAnchorEl(null); }}>
                <ExitToApp sx={{ mr: 1.5, fontSize: 18, color: 'error.main' }} />
                <Typography fontWeight="bold" color="error.main">{t('drawer.logout')}</Typography>
            </MenuItem>
        </Menu>

        {/* Edit Profile Dialog */}
        <Dialog open={profileOpen} onClose={() => setProfileOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {t('profile.title')}
                <IconButton size="small" onClick={() => setProfileOpen(false)}><Close /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                {profileError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProfileError('')}>{profileError}</Alert>
                )}
                {profileSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>{profileSuccess}</Alert>
                )}

                <TextField
                    fullWidth
                    label={t('profile.yourName')}
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    inputProps={{ maxLength: 100 }}
                    sx={{ mb: 3 }}
                />

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>{t('profile.changePinOptional')}</Typography>

                <TextField
                    fullWidth
                    label={t('profile.currentPin')}
                    type="password"
                    value={profileCurrentPin}
                    onChange={(e) => setProfileCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    sx={{ mb: 1.5 }}
                />
                <TextField
                    fullWidth
                    label={t('profile.newPin')}
                    type="password"
                    value={profileNewPin}
                    onChange={(e) => setProfileNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    sx={{ mb: 1.5 }}
                />
                <TextField
                    fullWidth
                    label={t('profile.confirmNewPin')}
                    type="password"
                    value={profileConfirmPin}
                    onChange={(e) => setProfileConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    sx={{ mb: 2.5 }}
                />

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={profileLoading}
                    onClick={async () => {
                        setProfileError('');
                        setProfileSuccess('');
                        const changingPin = profileNewPin.length > 0 || profileConfirmPin.length > 0;
                        if (changingPin) {
                            if (profileCurrentPin.length !== 6) { setProfileError('Enter your current 6-digit PIN'); return; }
                            if (profileNewPin.length !== 6)     { setProfileError('New PIN must be 6 digits'); return; }
                            if (profileNewPin !== profileConfirmPin) { setProfileError('PINs do not match'); return; }
                        }
                        setProfileLoading(true);
                        try {
                            const payload = {};
                            if (profileName.trim()) payload.name = profileName.trim();
                            else payload.name = null;
                            if (changingPin) {
                                payload.current_password = profileCurrentPin;
                                payload.new_password = profileNewPin;
                            }
                            const res = await authAPI.updateProfile(payload);
                            const updated = res.data;
                            setUser(prev => ({ ...prev, f_name: updated.f_name }));
                            if (updated.f_name) localStorage.setItem('user_name', updated.f_name);
                            else localStorage.removeItem('user_name');
                            setProfileSuccess('Profile saved!');
                            setTimeout(() => setProfileOpen(false), 1200);
                        } catch (err) {
                            setProfileError(err.response?.data?.detail || 'Failed to save profile');
                        } finally {
                            setProfileLoading(false);
                        }
                    }}
                >
                    {profileLoading ? <CircularProgress size={22} /> : t('profile.save')}
                </Button>
            </DialogContent>
        </Dialog>

        {/* Edit Business Dialog */}
        <Dialog open={editBizOpen} onClose={() => !editBizLoading && setEditBizOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Edit Business
                <IconButton size="small" onClick={() => setEditBizOpen(false)} disabled={editBizLoading}><Close /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                {editBizError && <Alert severity="error" sx={{ mb: 2 }}>{editBizError}</Alert>}
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth required label="Business Name"
                            value={editBizData.name || ''}
                            onChange={e => setEditBizData(p => ({ ...p, name: e.target.value }))}
                            inputProps={{ maxLength: 100 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth label="GST Number (Optional)"
                            value={editBizData.gst || ''}
                            onChange={e => setEditBizData(p => ({ ...p, gst: e.target.value }))}
                            inputProps={{ maxLength: 15 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth label="Phone (Optional)"
                            value={editBizData.phone || ''}
                            onChange={e => setEditBizData(p => ({ ...p, phone: e.target.value }))}
                            inputProps={{ maxLength: 15 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth label="UPI ID (Optional)"
                            placeholder="e.g. yourname@upi"
                            value={editBizData.upi_id || ''}
                            onChange={e => setEditBizData(p => ({ ...p, upi_id: e.target.value.trim() }))}
                            helperText="Customers can scan and pay directly from invoices"
                            inputProps={{ maxLength: 100 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth label="Address Line 1"
                            value={editBizData.address1 || ''}
                            onChange={e => setEditBizData(p => ({ ...p, address1: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth label="Address Line 2 (Optional)"
                            value={editBizData.address2 || ''}
                            onChange={e => setEditBizData(p => ({ ...p, address2: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField
                            fullWidth label="City"
                            value={editBizData.city || ''}
                            onChange={e => setEditBizData(p => ({ ...p, city: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth label="State"
                            value={editBizData.state || ''}
                            onChange={e => setEditBizData(p => ({ ...p, state: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth label="Pincode"
                            value={editBizData.pincode || ''}
                            onChange={e => setEditBizData(p => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>What kind of business do you run?</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {[
                                {
                                    value: 'subscription',
                                    emoji: '📦',
                                    title: 'Regular Delivery',
                                    subtitle: 'రోజూ డెలివరీ, నెల తర్వాత బిల్లు',
                                    desc: 'Milk, newspaper, water — delivered daily. Bill is generated only after the month is complete.',
                                },
                                {
                                    value: 'orders',
                                    emoji: '🛍️',
                                    title: 'Order by Order',
                                    subtitle: 'ఆర్డర్ వస్తే, డెలివరీ చేయండి',
                                    desc: 'Customer calls, you deliver and collect payment each time',
                                },
                                {
                                    value: 'both',
                                    emoji: '📦🛍️',
                                    title: 'Both',
                                    subtitle: 'రెగ్యులర్ డెలివరీ + ఒకసారి ఆర్డర్లు',
                                    desc: 'Run subscriptions and take individual orders together',
                                },
                            ].map(opt => {
                                const selected = (editBizData.business_mode || 'subscription') === opt.value;
                                return (
                                    <Paper
                                        key={opt.value}
                                        variant="outlined"
                                        onClick={() => setEditBizData(p => ({ ...p, business_mode: opt.value }))}
                                        sx={{
                                            p: 1.5,
                                            cursor: 'pointer',
                                            borderWidth: 2,
                                            borderColor: selected ? 'primary.main' : 'divider',
                                            bgcolor: selected ? 'primary.50' : 'background.paper',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.5,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '1.8rem', lineHeight: 1 }}>{opt.emoji}</Typography>
                                        <Box>
                                            <Typography variant="body1" fontWeight="bold" color={selected ? 'primary.main' : 'text.primary'}>
                                                {opt.title}
                                            </Typography>
                                            <Typography variant="body2" color={selected ? 'primary.dark' : 'text.secondary'} fontStyle="italic">
                                                {opt.subtitle}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {opt.desc}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                            <Box>
                                <Typography variant="body2" fontWeight="bold">Online Public Store</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Allow customers to browse and place orders via a public link
                                </Typography>
                            </Box>
                            <Switch
                                checked={editBizData.public_store_enabled || false}
                                onChange={e => setEditBizData(p => ({ ...p, public_store_enabled: e.target.checked }))}
                                color="primary"
                            />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setEditBizOpen(false)} disabled={editBizLoading}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveEditBiz} disabled={editBizLoading}>
                    {editBizLoading ? <CircularProgress size={20} /> : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>


        {/* Role Selection Dialog */}
        <Dialog open={roleSelectionDialog} onClose={() => {}} maxWidth="sm" fullWidth>
            <DialogTitle>Welcome to Vendora!</DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you here as a Vendor (business owner) or Customer (to place orders)?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<BusinessIcon />}
                        onClick={() => {
                            setRoleSelectionDialog(false);
                            setShowBusinessForm(true);
                        }}
                        sx={{ minWidth: 120 }}
                    >
                        I'm a Vendor
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<People />}
                        onClick={() => {
                            setRoleSelectionDialog(false);
                            setCurrentView('customer');
                        }}
                        sx={{ minWidth: 120 }}
                    >
                        I'm a Customer
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>

        {adminMode ? (
            <SuperAdminDashboard onBack={() => setAdminMode(false)} />
        ) : currentView === 'customer' ? (
            /* Customer View — CustomerDashboard owns its own AppBar + BottomNav */
            <CustomerDashboard
                user={user}
                vendors={businesses}
                onSwitchToVendor={() => setCurrentView('vendor')}
                themeMode={themeMode}
                onToggleTheme={onToggleTheme}
                onLogout={handleLogout}
                initialBusinessId={initialCustomerBusinessId}
            />
        ) : (
        <>
        <Container maxWidth="xl" sx={{ mt: 'calc(48px + env(safe-area-inset-top, 0px))', px: { xs: 1.5, sm: 3 }, pb: 10, minHeight: 'calc(100dvh - 48px - env(safe-area-inset-top, 0px))' }}>

            {businesses.length === 0 ? (
                /* No Business State */
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <Card elevation={2} sx={{ maxWidth: 420, width: '100%', mx: 2 }}>
                        <CardContent>
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <BusinessIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    {t('dashboard.noBusiness')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {t('dashboard.vendorNote')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {t('dashboard.createDesc')}
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    startIcon={<Add />}
                                    onClick={() => setShowBusinessForm(true)}
                                >
                                    {t('dashboard.createBusiness')}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            ) : selectedBusiness ? (
                /* Business Selected - Detailed View */
                <Box sx={{ pb: 2, maxWidth: 720, mx: 'auto' }}>
                            {/* Daily Ops Tab */}
                            {activeTab === 6 && (
                                <DailyOpsPage businessId={selectedBusinessId} business={selectedBusiness} members={businessMembers}
                                    onShowBars={() => setBarsVisible(true)} />
                            )}

                            {/* Billing & Invoicing Tab */}
                            {activeTab === 4 && (
                                <Box>
                                    <BillingPage
                                        businessId={selectedBusinessId}
                                        business={selectedBusiness}
                                        customers={businessCustomers}
                                        areas={businessAreas}
                                        products={businessProducts}
                                    />
                                </Box>
                            )}

                            {/* Dues Tab */}
                            {activeTab === 5 && showDuesTab && (
                                <Box>
                                    <DuesPage
                                        businessId={selectedBusinessId}
                                        business={selectedBusiness}
                                        // Land on Alerts when payment confirmations are waiting —
                                        // the nav badge is what drew them here. Remounts per visit,
                                        // so this re-evaluates each time Dues is opened.
                                        initialTab={duesAlertCount > 0 ? 2 : 0}
                                        onNotificationCountChange={setDuesAlertCount}
                                    />
                                </Box>
                            )}

                            {/* Customers Tab */}
                            {activeTab === 1 && (
                                <Box>
                                    <CustomerManager
                                        businessId={selectedBusinessId}
                                        business={selectedBusiness}
                                        onCustomersUpdated={handleCustomersUpdated}
                                        members={businessMembers}
                                        barsVisible={barsVisible}
                                        onDrawerChange={setCustomerDrawerOpen}
                                        isOwner={selectedBusiness?.f_created_by === user?.f_id}
                                    />
                                </Box>
                            )}

                            {/* Subscriptions Management Tab */}
                            {activeTab === 3 && (
                                <Box>
                                    <CustomerSubscriptionManager businessId={selectedBusinessId} />
                                </Box>
                            )}

                            {/* Settings Tab */}
                            {activeTab === 0 && (
                                <Box>
                                    {/* Sub-tab bar */}
                                    <Tabs
                                        value={settingsSubTab}
                                        onChange={(_, v) => { setSettingsSubTab(v); }}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                                    >
                                        <Tab label={`Products${shareAlertCount ? ` (${shareAlertCount})` : ''}`} value={0} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
                                        <Tab label="Areas" value={1} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
                                        <Tab label="Agents" value={2} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
                                        <Tab label="Business" value={3} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
                                        {showOrders && <Tab label="Order Settings" value={4} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />}
                                        {canRestoreDeleted && <Tab label="Deleted" value={5} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />}
                                    </Tabs>

                                    {/* Products sub-tab */}
                                    {settingsSubTab === 0 && (() => {
                                // Plans are rows in this same list. They are never sorted, filtered or
                                // counted on their own — each one is shown directly under the product it
                                // sells, so the list reads "Curd, and here is how Curd goes out".
                                const realProducts = businessProducts.filter(p => !p.f_parent_business_product_id);
                                const plansByParent = businessProducts.reduce((acc, p) => {
                                    if (p.f_parent_business_product_id) {
                                        (acc[p.f_parent_business_product_id] ||= []).push(p);
                                    }
                                    return acc;
                                }, {});

                                const showSearchSort = realProducts.length > 10;
                                const filteredProducts = realProducts.filter(p => {
                                    if (selectedCategoryFilter === NO_CATEGORY_FILTER) {
                                        if (productCategoryList(p).length) return false;
                                    } else if (selectedCategoryFilter && !productCategoryList(p).includes(selectedCategoryFilter)) return false;
                                    if (productSearch) {
                                        const q = productSearch.toLowerCase();
                                        // Match the product's own plans too, so searching "Sunday" finds
                                        // "Sunday Curd" by surfacing Curd rather than hiding both.
                                        const hit = p.f_name?.toLowerCase().includes(q)
                                            || (plansByParent[p.f_id] || []).some(pl => pl.f_name?.toLowerCase().includes(q));
                                        if (!hit) return false;
                                    }
                                    return true;
                                });
                                const sortedProducts = [...filteredProducts].sort((a, b) => {
                                    if (productSortBy === 'name_asc') return (a.f_name || '').localeCompare(b.f_name || '');
                                    if (productSortBy === 'name_desc') return (b.f_name || '').localeCompare(a.f_name || '');
                                    if (productSortBy === 'price_asc') return (a.current_price || 0) - (b.current_price || 0);
                                    if (productSortBy === 'price_desc') return (b.current_price || 0) - (a.current_price || 0);
                                    if (productSortBy === 'my_order') return (a.f_sort_order ?? 0) - (b.f_sort_order ?? 0) || (a.f_name || '').localeCompare(b.f_name || '');
                                    if (productSortBy === 'category') {
                                        // By the product's first category, in the vendor's category order
                                        // (unordered categories after, A-Z; no category last), then My order.
                                        const catRank = (p) => {
                                            const c = productCategoryList(p)[0];
                                            if (!c) return [2, ''];
                                            const i = productCategoryOrder.indexOf(c);
                                            return i >= 0 ? [0, String(i).padStart(4, '0')] : [1, c.toLowerCase()];
                                        };
                                        const [ga, ka] = catRank(a);
                                        const [gb, kb] = catRank(b);
                                        return (ga - gb) || ka.localeCompare(kb)
                                            || (a.f_sort_order ?? 0) - (b.f_sort_order ?? 0)
                                            || (a.f_name || '').localeCompare(b.f_name || '');
                                    }
                                    return (b.f_is_available ? 1 : 0) - (a.f_is_available ? 1 : 0); // 'available' default
                                });

                                return (
                                <Box>
                                    {/* ── Products another vendor shared with this business ── */}
                                    {pendingShares.map(sh => (
                                        <Box key={sh.f_id} sx={{ mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(25,118,210,0.06)',
                                                                 border: '1px solid', borderColor: 'primary.light' }}>
                                            <Typography variant="body2" fontWeight={800} sx={{ mb: 1 }}>
                                                📦 {sh.f_message}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button variant="outlined" onClick={() => dismissShare(sh)}
                                                    sx={{ minHeight: 44, textTransform: 'none' }}>Not now</Button>
                                                <Button variant="contained" disableElevation onClick={() => setOpenShare(sh)}
                                                    sx={{ flex: 1, minHeight: 44, textTransform: 'none', fontWeight: 800 }}>See products</Button>
                                            </Box>
                                        </Box>
                                    ))}

                                    {/* ── Quick Add Product ── */}
                                    <Box sx={{ mb: 2 }}>
                                        {/* Header: product counts, then the two management actions side by side */}
                                        <Box sx={{ mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {realProducts.filter(p => p.f_is_available).length} available · {realProducts.length} total
                                                </Typography>
                                                {realProducts.length > 0 && canRestoreDeleted && (
                                                    <Button size="small" startIcon={<ShareIcon />} onClick={() => setShareDialogOpen(true)}
                                                        sx={{ textTransform: 'none', fontWeight: 700, minHeight: 36 }}>
                                                        Share
                                                    </Button>
                                                )}
                                            </Box>
                                            {(bizMode === 'orders' || bizMode === 'both') && (
                                                <Button fullWidth size="small" variant="contained" disableElevation startIcon={<Add />}
                                                    onClick={() => setCataloguePickerOpen(true)}
                                                    sx={{ mb: 1 }}>
                                                    Add from list
                                                </Button>
                                            )}
                                            {realProducts.length > 0 && (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button fullWidth size="small" variant="outlined" startIcon={<span>₹</span>}
                                                        onClick={() => setBulkPriceOpen(true)}>
                                                        Update Prices
                                                    </Button>
                                                    <Button fullWidth size="small" variant="outlined"
                                                        onClick={() => setCategoryManagerOpen(true)}>
                                                        Sort & Categories
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Search + Sort — visible only when > 10 products */}
                                        {showSearchSort && (
                                            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                                <TextField
                                                    size="small"
                                                    placeholder="Search products…"
                                                    value={productSearch}
                                                    onChange={e => setProductSearch(e.target.value)}
                                                    sx={{
                                                        flex: '0 0 66%',
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 3,
                                                            bgcolor: 'white',
                                                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                                            border: '1.5px solid',
                                                            borderColor: productSearch ? 'primary.main' : 'grey.200',
                                                            transition: 'border-color 0.2s, box-shadow 0.2s',
                                                            '& fieldset': { border: 'none' },
                                                            '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' },
                                                            '&.Mui-focused': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(37,99,235,0.12)' },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon fontSize="small" sx={{ color: productSearch ? 'primary.main' : 'text.disabled' }} />
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment: productSearch ? (
                                                            <InputAdornment position="end">
                                                                <IconButton size="small" onClick={() => setProductSearch('')} sx={{ color: 'text.secondary', p: 0.5 }}>
                                                                    <ClearIcon fontSize="small" />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ) : null,
                                                    }}
                                                />
                                                <FormControl size="small" sx={{ flexShrink: 0, width: 'calc(34% - 8px)' }}>
                                                    <Select
                                                        value={productSortBy}
                                                        onChange={e => setProductSortBy(e.target.value)}
                                                        displayEmpty
                                                        renderValue={v => {
                                                            const labels = { available: 'Available', my_order: 'My order', category: 'Category', name_asc: 'A → Z', name_desc: 'Z → A', price_asc: 'Price ↑', price_desc: 'Price ↓' };
                                                            return (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>Sort by</Typography>
                                                                    <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{labels[v] || v}</Typography>
                                                                </Box>
                                                            );
                                                        }}
                                                        sx={{
                                                            borderRadius: 3,
                                                            bgcolor: 'white',
                                                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                                            border: '1.5px solid',
                                                            borderColor: productSortBy !== 'my_order' ? 'primary.main' : 'grey.200',
                                                            '& fieldset': { border: 'none' },
                                                            '&:hover': { borderColor: 'primary.light' },
                                                            '& .MuiSelect-select': { py: '6px' },
                                                        }}
                                                    >
                                                        <MenuItem value="available">Available first</MenuItem>
                                                        <MenuItem value="my_order">My order</MenuItem>
                                                        <MenuItem value="category">Category</MenuItem>
                                                        <MenuItem value="name_asc">A → Z</MenuItem>
                                                        <MenuItem value="name_desc">Z → A</MenuItem>
                                                        <MenuItem value="price_asc">Price ↑</MenuItem>
                                                        <MenuItem value="price_desc">Price ↓</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        )}

                                        {/* Category filter chips + Manage button */}
                                        {(() => {
                                            // In the vendor's Sort & Categories order (as everywhere else); any
                                            // category not in that order follows A–Z. "No category" is added last.
                                            const allCats = [...new Set(businessProducts.flatMap(p => productCategoryList(p)))];
                                            const distinctCats = [
                                                ...productCategoryOrder.filter(c => allCats.includes(c)),
                                                ...allCats.filter(c => !productCategoryOrder.includes(c))
                                                    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })),
                                            ];
                                            // Products the vendor has not put in any category yet — the
                                            // ones most worth finding, so they get a chip of their own.
                                            const hasUncategorised = realProducts.some(p => !productCategoryList(p).length);
                                            return distinctCats.length > 0 ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                    <Chip label="All" size="small"
                                                        color={selectedCategoryFilter === '' ? 'primary' : 'default'}
                                                        onClick={() => setSelectedCategoryFilter('')} clickable />
                                                    {distinctCats.map(cat => (
                                                        <Chip key={cat} label={cat} size="small"
                                                            color={selectedCategoryFilter === cat ? 'primary' : 'default'}
                                                            onClick={() => setSelectedCategoryFilter(selectedCategoryFilter === cat ? '' : cat)}
                                                            clickable />
                                                    ))}
                                                    {hasUncategorised && (
                                                        <Chip label="No category" size="small"
                                                            color={selectedCategoryFilter === NO_CATEGORY_FILTER ? 'primary' : 'default'}
                                                            onClick={() => setSelectedCategoryFilter(selectedCategoryFilter === NO_CATEGORY_FILTER ? '' : NO_CATEGORY_FILTER)}
                                                            clickable />
                                                    )}
                                                </Box>
                                            ) : null;
                                        })()}
                                    </Box>

                                    {/* ── Add Product ── */}
                                    <ProductFormDialog
                                        open={quickProductOpen}
                                        onClose={() => setQuickProductOpen(false)}
                                        mode="add"
                                        businessId={selectedBusinessId}
                                        bizMode={bizMode}
                                        categoryOptions={orderedCategoryOptions}
                                        onSaved={handleProductsUpdated}
                                    />

                                    {planOpsError && (
                                        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setPlanOpsError('')}>{planOpsError}</Alert>
                                    )}

                                    {/* ── Product cards ── */}
                                    <Box sx={{ mb: 3 }}>
                                        {sortedProducts.length === 0 ? (
                                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                                <Inventory sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                                {selectedCategoryFilter ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t('dashboard.noProductsCategory')}
                                                    </Typography>
                                                ) : (
                                                    <Box sx={{ maxWidth: 320, mx: 'auto', textAlign: 'left', mt: 1 }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                            ఇంకా ఏ ప్రొడక్టూ లేదు. <strong>+ Add Product</strong> నొక్కి మొదలుపెట్టండి.<br/>
                                                            <Typography variant="caption" color="text.disabled">No products yet. Tap + Add Product to get started.</Typography>
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                                <Typography variant="caption" sx={{ minWidth: 20 }}>📦</Typography>
                                                                <Typography variant="caption" color="text.secondary"><strong>ప్రొడక్ట్ పేరు</strong> — మీరు అమ్మేది (ఉదా. Amul పాలు, Eenadu పేపర్, వాటర్ క్యాన్)<br/><Typography variant="caption" color="text.disabled">Product name — what you sell (e.g. Amul Milk, Eenadu Paper, Water Can)</Typography></Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                                <Typography variant="caption" sx={{ minWidth: 20 }}>💰</Typography>
                                                                <Typography variant="caption" color="text.secondary"><strong>రేట్ టైప్</strong> — <em>Per Item</em> (డెలివరీకి చార్జ్) లేదా <em>Monthly Rate</em> (నెల ఫ్లాట్ ఫీ)<br/><Typography variant="caption" color="text.disabled">Rate type — Per Item or Monthly flat fee</Typography></Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                                <Typography variant="caption" sx={{ minWidth: 20 }}>🗓️</Typography>
                                                                <Typography variant="caption" color="text.secondary"><strong>అమ్మే విధానం</strong> — <em>Plans</em> (రోజూ డెలివరీ, నెల తర్వాత బిల్లు) లేదా <em>Orders only</em> (ఒకసారి ఆర్డర్)<br/><Typography variant="caption" color="text.disabled">Sold via — Plans (recurring) or Orders only (one-time)</Typography></Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                                <Typography variant="caption" sx={{ minWidth: 20 }}>🤝</Typography>
                                                                <Typography variant="caption" color="text.secondary"><strong>షార్ట్‌కట్</strong> — మీకు తెలిసిన వెండర్‌ని వాళ్ల Products పేజీలో <em>Share</em> నొక్కమని అడగండి. వాళ్ల ప్రొడక్ట్స్ ఇక్కడ కనిపిస్తాయి.<br/><Typography variant="caption" color="text.disabled">Shortcut — ask a vendor you know to tap Share on their Products page. Their products will show up here for you to pick.</Typography></Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        ) : (
                                            <Box>
                                                {sortedProducts.map((product) => {
                                                    const name = product.f_name || 'Unknown Product';
                                                    const billingType = product.f_billing_type;
                                                    const billingLabel = billingType === 'MonthlyFlatFee' ? 'Monthly Rate' : billingType === 'PerEachItem' ? 'Per Item' : null;
                                                    const billingUnit = billingType === 'MonthlyFlatFee' ? '/ mo' : billingType === 'PerEachItem' ? '/ item' : '';
                                                    const categoryLabel = product.f_business_product_category || product.category_name || null;
                                                    const productPlans = plansByParent[product.f_id] || [];
                                                    // Whether we offer to make a NEW plan. Per-item only — a monthly fee
                                                    // costs the same however many days you deliver, so a plan on one would
                                                    // look like it changed the bill when it didn't. Also plan-sold only, so
                                                    // an order-only catalogue isn't cluttered with an option that makes no
                                                    // sense there. The backend is looser; this is just what we suggest.
                                                    // NOTE: this gates only the "add" button. Existing plans render off
                                                    // productPlans.length alone, never off this — a plan that can't be
                                                    // seen can't be edited, deleted or have customers added, and it
                                                    // would still be billing them.
                                                    const canAddPlan = showSubscription
                                                        && billingType === 'PerEachItem'
                                                        && !product.f_is_delivery_product
                                                        && [1, 2].includes(product.f_selling_mode);
                                                    return (
                                                        <ListCard key={product.f_id} muted={!product.f_is_available} sx={{ mb: 1.5 }}>
                                                                {/* Row 1: name + status chip + archive */}
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <ProductImage src={product.f_image_url} name={name}
                                                                        onClick={product.f_image_url ? () => setPreviewImg(product.f_image_url) : undefined}
                                                                        fontSize="0.8rem"
                                                                        sx={{ width: 36, height: 36, borderRadius: 1, mr: 1, flexShrink: 0, cursor: product.f_image_url ? 'pointer' : 'default' }} />
                                                                    <Typography variant="subtitle1" noWrap sx={{ flex: 1, pr: 1 }}>{name}</Typography>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                                                    <Chip
                                                                        label={product.f_is_available ? t('dashboard.on') : t('dashboard.off')}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color={product.f_is_available ? 'success' : 'default'}
                                                                        clickable
                                                                        onClick={async () => {
                                                                            const newVal = !product.f_is_available;
                                                                            setBusinessProducts(prev => prev.map(p => p.f_id === product.f_id ? { ...p, f_is_available: newVal } : p));
                                                                            try {
                                                                                await businessAPI.updateBusinessProduct(product.f_id, { f_is_available: newVal });
                                                                            } catch {
                                                                                setBusinessProducts(prev => prev.map(p => p.f_id === product.f_id ? { ...p, f_is_available: !newVal } : p));
                                                                            }
                                                                        }}
                                                                        sx={{ fontWeight: 700, flexShrink: 0 }}
                                                                    />
                                                                    {!product.f_is_delivery_product && (
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleArchiveProductClick(product)}
                                                                            sx={{ color: 'error.main', p: 0.5 }}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    )}
                                                                    </Box>
                                                                </Box>
                                                                {/* Row 2: price and read-only facts on one line; chips only for
                                                                    states that must pop. */}
                                                                <MetaLine
                                                                    sx={{ mt: 0.5 }}
                                                                    items={[
                                                                        product.current_price
                                                                            ? (product.current_discount > 0
                                                                                ? <><s>₹{product.current_price}</s>{' '}
                                                                                    <Box component="strong" sx={{ color: 'text.primary' }}>
                                                                                        ₹{(product.current_price * (1 - product.current_discount / 100)).toFixed(2)}
                                                                                    </Box>{' '}{billingUnit}</>
                                                                                : `₹${product.current_price} ${billingUnit}`)
                                                                            : t('dashboard.noPriceSet'),
                                                                        billingLabel,
                                                                        // Set delivery days on the product itself (weekly/monthly papers).
                                                                        !product.f_parent_business_product_id && product.schedule_label && product.schedule_label !== 'Every day'
                                                                            ? <Box component="strong" sx={{ color: '#6a1b9a' }}>{product.schedule_label}</Box>
                                                                            : null,
                                                                        categoryLabel,
                                                                    ]}
                                                                    trailing={<>
                                                                        {product.f_is_delivery_product && (
                                                                            <Chip label="Auto-managed" size="small" variant="outlined" color="warning" />
                                                                        )}
                                                                        {product.current_discount > 0 && (
                                                                            <Chip label={`${product.current_discount}% off`} size="small" variant="outlined" color="success" sx={{ fontWeight: 700 }} />
                                                                        )}
                                                                    </>}
                                                                />
                                                                {/* Row 3: one solid button, the rest tinted. Hue says which
                                                                    action, saturation says which one matters. */}
                                                                <CardActions actions={[
                                                                    {
                                                                        key: 'customers', label: '+ Customers', primary: true,
                                                                        // The count rides beside the icon on the top line, where there is
                                                                        // room to spare — next to the label it spilled on small phones.
                                                                        // !important: CardActions styles its icon slot as a single block icon.
                                                                        icon: (
                                                                            <Box component="span" sx={{ display: 'inline-flex !important', alignItems: 'center' }}>
                                                                                <People sx={{ fontSize: '1.05rem' }} />
                                                                                <CustomerCount n={product.active_customer_count} onPrimary />
                                                                            </Box>
                                                                        ),
                                                                        hidden: !(product.f_is_available && !product.f_is_archived && [1, 2].includes(product.f_selling_mode)),
                                                                        onClick: () => handleOpenPlanDialog(product),
                                                                    },
                                                                    {
                                                                        key: 'price', label: 'Price', tone: 'primary', icon: <CurrencyRupee />,
                                                                        onClick: () => setPriceDialogProduct(product),
                                                                    },
                                                                    {
                                                                        key: 'discount', label: 'Discount', tone: 'success', icon: <LocalOfferIcon />,
                                                                        onClick: () => setDiscountDialogProduct(product),
                                                                    },
                                                                    {
                                                                        key: 'info', label: 'Info', tone: 'neutral', icon: <Edit />,
                                                                        hidden: product.f_is_delivery_product,
                                                                        onClick: () => setEditingProduct(product),
                                                                    },
                                                                ]} />

                                                                {/* ── Plans built on this product ──
                                                                    Inside the product's own card, because that is the
                                                                    relationship: same item, same price, fewer days.
                                                                    Safe to call these "plans" now that the button above
                                                                    says "Customers" — one word, one meaning, per control. */}
                                                                {productPlans.length > 0 && (
                                                                    <>
                                                                        <Divider sx={{ mt: 1.25, mb: 0.5 }} />
                                                                        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                                                                            Product Plans
                                                                        </Typography>
                                                                        {productPlans.map((plan) => (
                                                                            <Box key={plan.f_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.6 }}>
                                                                                <CalendarMonth sx={{ fontSize: '1.05rem', color: 'primary.main', flexShrink: 0 }} />
                                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                    <Typography variant="body2" fontWeight={600} noWrap>{plan.f_name}</Typography>
                                                                                    <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 700 }}>
                                                                                        {plan.schedule_label}
                                                                                    </Typography>
                                                                                </Box>
                                                                                {/* Same action as the product's "+ Customers" above — that is
                                                                                    the point of a set-day delivery, so it must be as reachable. */}
                                                                                <Button variant="outlined" size="small"
                                                                                    onClick={() => handleOpenPlanDialog(plan)}
                                                                                    sx={{
                                                                                        px: 1.25, flexShrink: 0, minHeight: TOUCH.dense,
                                                                                        borderColor: 'divider', color: 'text.primary',
                                                                                        '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
                                                                                    }}
                                                                                >Customers<CustomerCount n={plan.active_customer_count} /></Button>
                                                                                <IconButton size="small" onClick={() => setPlanForm({ plan })}
                                                                                    sx={{ p: 0.5, color: 'text.secondary' }}>
                                                                                    <Edit sx={{ fontSize: '1rem' }} />
                                                                                </IconButton>
                                                                                <IconButton size="small" onClick={() => handleArchiveProductClick(plan)}
                                                                                    sx={{ p: 0.5, color: 'error.main' }}>
                                                                                    <DeleteIcon sx={{ fontSize: '1rem' }} />
                                                                                </IconButton>
                                                                            </Box>
                                                                        ))}
                                                                    </>
                                                                )}
                                                                {canAddPlan && (
                                                                    <Button size="small" startIcon={<Add sx={{ fontSize: '0.9rem !important' }} />}
                                                                        onClick={() => setPlanForm({ product })}
                                                                        sx={{ mt: productPlans.length ? 0.25 : 0.75, px: 0.5 }}
                                                                    >
                                                                        {productPlans.length ? 'Add another' : 'Deliver on set days'}
                                                                    </Button>
                                                                )}
                                                        </ListCard>
                                                    );
                                                })}
                                                {/* Clears the extended FAB, which floats above this list. */}
                                                <Box sx={{ height: 72 }} />
                                            </Box>
                                        )}
                                    </Box>

                                </Box>
                                );
                            })()}
                                    {settingsSubTab === 1 && (
                                        <Box>
                                            <Box sx={{ mb: 1.5 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {businessAreas.length} area{businessAreas.length !== 1 ? 's' : ''}
                                                </Typography>
                                            </Box>

                                            {areaOpsError && (
                                                <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setAreaOpsError('')}>{areaOpsError}</Alert>
                                            )}

                                            {areaShowAddForm && (
                                                <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                                                    <TextField fullWidth label="Area Name" value={newAreaName}
                                                        onChange={e => setNewAreaName(e.target.value)}
                                                        size="small" placeholder="e.g. Sector A, Block B"
                                                        onKeyDown={e => e.key === 'Enter' && handleAreaAdd()}
                                                        sx={{ mb: 0.5 }} autoFocus />
                                                    {bizMode !== 'subscription' && (
                                                        <TextField fullWidth size="small"
                                                            label="Delivery Charge"
                                                            type="number"
                                                            value={newAreaDeliveryCharge}
                                                            onChange={e => setNewAreaDeliveryCharge(e.target.value)}
                                                            inputProps={{ min: 0, step: 1 }}
                                                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                                            helperText="Leave blank = use default · 0 = free delivery"
                                                            sx={{ mb: 0.5 }}
                                                        />
                                                    )}
                                                    <GpsSelector
                                                        existingName={null}
                                                        picked={addAreaPicked}
                                                        cleared={false}
                                                        onPick={setAddAreaPicked}
                                                        onClear={() => setAddAreaPicked(null)}
                                                        onRemoveExisting={() => {}}
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                                                        <Button size="small" variant="contained" onClick={handleAreaAdd}
                                                            disabled={areaOpsLoading || !newAreaName.trim()}>Add</Button>
                                                        <Button size="small" onClick={() => { setAreaShowAddForm(false); setNewAreaName(''); setAddAreaPicked(null); }}>Cancel</Button>
                                                    </Box>
                                                </Box>
                                            )}

                                            {businessAreas.length > 1 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                    Drag the handle on the left to set which area the round covers first.
                                                </Typography>
                                            )}

                                            {businessAreas.length === 0 && !areaShowAddForm ? (
                                                <Box>
                                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                                        <MapIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
                                                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>No areas yet</Typography>
                                                        <Typography variant="body2" color="text.secondary">Group customers by delivery zone — e.g. Sector A, Block B.</Typography>
                                                    </Box>
                                                    {/* With no areas there is still a round to arrange, so offer the
                                                        delivery order over every customer. */}
                                                    {noAreaCustomers.length > 0 && (
                                                        <Box sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', p: 1.5 }}>
                                                            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>All Customers</Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                <Chip label={`${noAreaCustomers.length} customer${noAreaCustomers.length !== 1 ? 's' : ''}`}
                                                                    size="small" variant="outlined" sx={{ fontSize: '0.72rem', height: 22 }} />
                                                                <Button size="small" variant="contained" disableElevation
                                                                    startIcon={<FormatListNumbered sx={{ fontSize: 16 }} />}
                                                                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1.5 }}
                                                                    onClick={() => setRouteOrderArea({ id: null, name: 'All Customers' })}>
                                                                    Delivery Order
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                  <DndContext sensors={areaDragSensors} collisionDetection={closestCenter} onDragEnd={handleAreaDragEnd}>
                                                   <SortableContext items={businessAreas.map(a => a.f_id)} strategy={verticalListSortingStrategy}>
                                                    {businessAreas.map(area => {
                                                        const areaCustomers = businessCustomers.filter(c => c.f_area_id === area.f_id && !c.f_is_archived);
                                                        return (
                                                            <SortableAreaCard key={area.f_id} id={area.f_id}
                                                                disabled={businessAreas.length < 2 || areaEditingId === area.f_id}>
                                                                {/* Row 1: area name (or edit field) + edit/delete.
                                                                    Padding comes from ListCard. */}
                                                                <Box>
                                                                    {areaEditingId === area.f_id ? (
                                                                        <>
                                                                            <TextField value={areaEditName}
                                                                                onChange={e => setAreaEditName(e.target.value)}
                                                                                size="small" fullWidth autoFocus
                                                                                label="Area Name"
                                                                                onKeyDown={e => e.key === 'Enter' && handleAreaSaveEdit()}
                                                                                sx={{ mb: 0.5 }} />
                                                                            <GpsSelector
                                                                                existingName={editAreaExistingName}
                                                                                picked={editAreaPicked}
                                                                                cleared={editAreaCleared}
                                                                                onPick={setEditAreaPicked}
                                                                                onClear={() => { setEditAreaPicked(null); setEditAreaCleared(true); setEditAreaExistingName(null); }}
                                                                                onRemoveExisting={() => { setEditAreaExistingName(null); setEditAreaCleared(true); }}
                                                                            />
                                                                            {bizMode !== 'subscription' && (
                                                                                <TextField fullWidth size="small"
                                                                                    label="Delivery Charge"
                                                                                    type="number"
                                                                                    value={editAreaDeliveryCharge}
                                                                                    onChange={e => setEditAreaDeliveryCharge(e.target.value)}
                                                                                    inputProps={{ min: 0, step: 1 }}
                                                                                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                                                                    helperText="Leave blank = use default · 0 = free delivery"
                                                                                    sx={{ mt: 0.5 }}
                                                                                />
                                                                            )}
                                                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                                                <Button size="small" variant="contained" disableElevation
                                                                                    onClick={handleAreaSaveEdit}
                                                                                    disabled={areaOpsLoading || !areaEditName.trim()}
                                                                                    sx={{ textTransform: 'none' }}>
                                                                                    {areaOpsLoading ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} /> : null}
                                                                                    Save
                                                                                </Button>
                                                                                <Button size="small" onClick={() => { setAreaEditingId(null); setEditAreaPicked(null); setEditAreaExistingName(null); setEditAreaCleared(false); setEditAreaDeliveryCharge(''); }}
                                                                                    sx={{ textTransform: 'none' }}>
                                                                                    Cancel
                                                                                </Button>
                                                                            </Box>
                                                                        </>
                                                                    ) : (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                    {area.f_latitude && <LocationOn sx={{ fontSize: 15, color: 'text.secondary' }} />}
                                                                                    <Typography variant="body2" fontWeight={600} noWrap>{area.f_name}</Typography>
                                                                                </Box>
                                                                                {area.f_community_name && (
                                                                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                                                        {area.f_community_name}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                            <IconButton size="small" onClick={() => { setAreaEditingId(area.f_id); setAreaEditName(area.f_name); setEditAreaExistingName(area.f_community_name || null); setEditAreaPicked(null); setEditAreaCleared(false); setEditAreaDeliveryCharge(area.f_delivery_charge != null ? String(area.f_delivery_charge) : ''); setAreaOpsError(''); }}>
                                                                                <Edit fontSize="small" />
                                                                            </IconButton>
                                                                            <IconButton size="small" color="error" onClick={() => handleAreaDelete(area.f_id)} disabled={areaOpsLoading}>
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                                {/* Row 2: the same facts-then-actions shape as a product card. */}
                                                                {areaEditingId !== area.f_id && (
                                                                    <>
                                                                        <MetaLine sx={{ mt: 0.5 }} items={[
                                                                            `${areaCustomers.length} customer${areaCustomers.length !== 1 ? 's' : ''}`,
                                                                            bizMode !== 'subscription' && (
                                                                                area.f_delivery_charge == null ? 'Default charge'
                                                                                    : area.f_delivery_charge === 0 ? 'Free delivery'
                                                                                        : `₹${area.f_delivery_charge} delivery`
                                                                            ),
                                                                        ]} />
                                                                        <CardActions actions={[
                                                                            {
                                                                                key: 'add', label: '+ Add Customers', icon: <People />, primary: true,
                                                                                onClick: () => setAreaAssignId(area.f_id),
                                                                            },
                                                                            {
                                                                                key: 'order', label: 'Delivery Order', tone: 'neutral',
                                                                                icon: <FormatListNumbered />,
                                                                                hidden: areaCustomers.length <= 1,
                                                                                onClick: () => setRouteOrderArea({ id: area.f_id, name: area.f_name }),
                                                                            },
                                                                        ]} />
                                                                    </>
                                                                )}
                                                            </SortableAreaCard>
                                                        );
                                                    })}
                                                   </SortableContext>
                                                  </DndContext>

                                                    {/* Customers not in any area are delivered last, and still need
                                                        an order among themselves. */}
                                                    {noAreaCustomers.length > 0 && (() => {
                                                        // Mid-way through adding the very first area there are still no areas,
                                                        // so "No Area" would be a lie — call the bucket what it is.
                                                        const label = businessAreas.length === 0 ? 'All Customers' : 'No Area';
                                                        return (
                                                        <ListCard dashed>
                                                            <Typography variant="body2" fontWeight={600}>{label}</Typography>
                                                            {businessAreas.length > 0 && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                    Delivered last
                                                                </Typography>
                                                            )}
                                                            <MetaLine sx={{ mt: 0.5 }} items={[
                                                                `${noAreaCustomers.length} customer${noAreaCustomers.length !== 1 ? 's' : ''}`,
                                                            ]} />
                                                            <CardActions actions={[{
                                                                key: 'order', label: 'Delivery Order', tone: 'neutral',
                                                                icon: <FormatListNumbered />,
                                                                hidden: noAreaCustomers.length <= 1,
                                                                onClick: () => setRouteOrderArea({ id: null, name: label }),
                                                            }]} />
                                                        </ListCard>
                                                        );
                                                    })()}
                                                    {/* Clears the extended FAB, which floats above this list. */}
                                                    <Box sx={{ height: 72 }} />
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Agents sub-tab */}
                                    {settingsSubTab === 2 && (
                                        <Box>
                                            <Box sx={{ mb: 1.5 }}>
                                                {/* The owner is a real row in this list now, but they are not an agent. */}
                                                {(() => {
                                                    const n = businessMembers.filter(m => m.f_role !== 'Owner').length;
                                                    return (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {n} agent{n !== 1 ? 's' : ''}
                                                        </Typography>
                                                    );
                                                })()}
                                            </Box>

                                            {memberOpsError && (
                                                <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setMemberOpsError('')}>{memberOpsError}</Alert>
                                            )}
                                            {memberOpsSuccess && (
                                                <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setMemberOpsSuccess('')}>{memberOpsSuccess}</Alert>
                                            )}

                                            {memberShowAddForm && (
                                                <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                                                    <TextField label="Agent name" value={memberNameInput}
                                                        onChange={e => setMemberNameInput(e.target.value)}
                                                        size="small" fullWidth sx={{ mb: 1 }}
                                                        placeholder="Ramesh" />
                                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                        <TextField label="Phone number" value={memberMobileInput}
                                                            onChange={e => { setMemberMobileInput(e.target.value); setMemberLookupResult(null); setMemberLookupError(''); }}
                                                            size="small" sx={{ flex: 1 }}
                                                            type="tel" inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                                                            onKeyDown={e => e.key === 'Enter' && handleMemberLookup()} />
                                                        {/* Optional check — the agent does not need an account to be added. */}
                                                        <Button variant="outlined" size="small" onClick={handleMemberLookup}
                                                            disabled={memberLookupLoading || memberMobileInput.trim().length < 10}
                                                            sx={{ minHeight: TOUCH.dense, whiteSpace: 'nowrap' }}>
                                                            {memberLookupLoading ? <CircularProgress size={18} /> : 'Check'}
                                                        </Button>
                                                    </Box>
                                                    {/* Read the digits back in pairs — the one cheap guard against a wrong number
                                                        quietly giving a stranger access once they sign up. */}
                                                    {memberMobileInput.trim().length === 10 && (
                                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, letterSpacing: 1 }}>
                                                            {memberMobileInput.trim().replace(/(\d{5})(\d{5})/, '$1 $2')}
                                                        </Typography>
                                                    )}
                                                    {memberLookupError && (
                                                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>{memberLookupError}</Typography>
                                                    )}
                                                    {memberLookupResult && (
                                                        <Box sx={{ mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                            {memberLookupResult.found ? (
                                                                <Typography variant="body2">
                                                                    This number is <strong>{memberLookupResult.f_name || memberLookupResult.f_mobile}</strong> on Vendora.
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Not on Vendora yet — you can still add them. They will be linked when they join.
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    )}
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <Select value={memberSelectedRole} onChange={e => setMemberSelectedRole(e.target.value)}
                                                            size="small" sx={{ minWidth: 100 }}>
                                                            <MenuItem value="Agent">Agent</MenuItem>
                                                            <MenuItem value="Admin">Admin</MenuItem>
                                                        </Select>
                                                        <Button size="small" variant="contained" onClick={handleMemberAdd}
                                                            disabled={memberAddLoading || memberMobileInput.trim().length < 10}
                                                            sx={{ minHeight: TOUCH.dense }}>
                                                            {memberAddLoading ? <CircularProgress size={18} /> : 'Add'}
                                                        </Button>
                                                        <Button size="small" sx={{ minHeight: TOUCH.dense }}
                                                            onClick={() => { setMemberShowAddForm(false); setMemberMobileInput(''); setMemberNameInput(''); setMemberLookupResult(null); setMemberLookupError(''); }}>Cancel</Button>
                                                    </Box>
                                                </Box>
                                            )}

                                            {selectedBusiness?.f_created_by !== user?.f_id ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                                    Only the business owner can manage agents.
                                                </Typography>
                                            ) : businessMembers.filter(m => m.f_role !== 'Owner').length === 0 && !memberShowAddForm ? (
                                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                                    <GroupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
                                                    <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>No agents yet</Typography>
                                                    <Typography variant="body2" color="text.secondary">Add an agent with their name and phone number. They do not need the app yet.</Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {businessMembers.map(m => {
                                                        // f_agent_id holds the member row id, so it matches m.f_id.
                                                        const memberCustomers = businessCustomers.filter(c => c.f_agent_id === m.f_id && !c.f_is_archived);
                                                        const isOwner = m.f_role === 'Owner';
                                                        const displayName = m.f_name || m.f_mobile;
                                                        const displayMobile = m.f_name ? m.f_mobile : null;
                                                        return (
                                                        <ListCard key={m.f_id}>
                                                            {/* Row 1: name + role + delete. Padding comes from ListCard. */}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography variant="body2" fontWeight={600} noWrap>{displayName}</Typography>
                                                                    {displayMobile && (
                                                                        <Typography variant="caption" color="text.secondary" noWrap>{displayMobile}</Typography>
                                                                    )}
                                                                </Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
                                                                    {isOwner ? (
                                                                        <Chip label="Owner" size="small" color="primary" variant="outlined" />
                                                                    ) : (
                                                                        <>
                                                                            {memberRoleChanging[m.f_id] ? (
                                                                                <CircularProgress size={18} />
                                                                            ) : (
                                                                                <Select value={m.f_role} size="small"
                                                                                    sx={{ minWidth: 85, fontSize: '0.75rem' }}
                                                                                    onChange={e => handleMemberRoleChange(m, e.target.value)}>
                                                                                    <MenuItem value="Agent">Agent</MenuItem>
                                                                                    <MenuItem value="Admin">Admin</MenuItem>
                                                                                </Select>
                                                                            )}
                                                                            <IconButton size="small" color="error"
                                                                                onClick={() => handleMemberRemove(m)}>
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                            {/* "Not joined yet" is load-bearing, not decoration: if a digit was mistyped
                                                                the vendor sees either an unexpected name or a chip that never clears. */}
                                                            {!isOwner && !m.f_user_id && (
                                                                <Chip label="Not joined yet" size="small" variant="outlined"
                                                                    sx={{ mt: 0.5, height: 22, fontSize: '0.7rem' }} />
                                                            )}
                                                            {/* Row 2: same facts-then-actions shape as products and areas. */}
                                                            <MetaLine sx={{ mt: 0.5 }} items={[
                                                                `${memberCustomers.length} customer${memberCustomers.length !== 1 ? 's' : ''}`,
                                                            ]} />
                                                            <CardActions actions={[
                                                                {
                                                                    key: 'add', label: '+ Add Customers', icon: <People />, primary: true,
                                                                    onClick: () => setAgentAssignId(m.f_id),
                                                                },
                                                                // Short label on purpose: a two-button row hugs its labels
                                                                // and a secondary button only gets ~72px at 390px.
                                                                ...(isOwner ? [] : [{
                                                                    key: 'edit', label: 'Name & Number', tone: 'neutral', icon: <Edit />,
                                                                    onClick: () => {
                                                                        setMemberEditTarget(m);
                                                                        setMemberEditName(m.f_name || '');
                                                                        setMemberEditMobile(m.f_mobile || '');
                                                                        setMemberEditError('');
                                                                    },
                                                                }]),
                                                            ]} />
                                                        </ListCard>
                                                        );
                                                    })}
                                                    {/* Clears the extended FAB, which floats above this list. */}
                                                    <Box sx={{ height: 72 }} />
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Order Settings sub-tab */}
                                    {/* Deleted customers and products, with Restore (owner/admin) */}
                                    {settingsSubTab === 5 && canRestoreDeleted && (
                                        <DeletedItemsPage
                                            businessId={selectedBusinessId}
                                            areas={businessAreas}
                                            // Deleting forever is the owner's call alone
                                            canDeleteForever={selectedBusiness?.f_created_by === user?.f_id}
                                            onRestored={kind => (kind === 'customers'
                                                ? loadBusinessCustomers(selectedBusinessId)
                                                : loadBusinessProducts(selectedBusinessId))}
                                        />
                                    )}

                                    {settingsSubTab === 4 && showOrders && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {orderSettingsLoading ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                                            ) : (
                                                <>
                                                    {orderSettingsError && <Alert severity="error" onClose={() => setOrderSettingsError('')}>{orderSettingsError}</Alert>}
                                                    {orderSettingsSuccess && <Alert severity="success" onClose={() => setOrderSettingsSuccess('')}>{orderSettingsSuccess}</Alert>}

                                                    {/* ── Group switcher (inline) — pick which set of settings to view ── */}
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                                                        Settings for
                                                    </Typography>
                                                    <ToggleButtonGroup exclusive fullWidth size="small"
                                                        value={orderSettingsView}
                                                        onChange={(_, v) => v && setOrderSettingsView(v)}
                                                        sx={{ '& .MuiToggleButton-root': { flex: 1, textTransform: 'none', fontSize: '0.72rem', fontWeight: 600, py: 0.9, px: 0.5, lineHeight: 1.2, borderRadius: '10px !important', border: '1.5px solid !important', borderColor: 'divider !important', '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', borderColor: 'primary.main !important' } } }}
                                                    >
                                                        <ToggleButton value="online">Online Orders</ToggleButton>
                                                        <ToggleButton value="vendor">Vendor Orders</ToggleButton>
                                                        <ToggleButton value="common">Common</ToggleButton>
                                                    </ToggleButtonGroup>

                                                    {orderSettingsView === 'online' && (<>
                                                    {/* ── Public Store card ── */}
                                                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                        <Box sx={{ px: 2, py: 1.25, bgcolor: bizDetailStorePublic ? '#e8f5e9' : '#f3f4f6', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Inventory sx={{ fontSize: 16, color: bizDetailStorePublic ? '#2e7d32' : 'text.secondary' }} />
                                                                <Typography variant="body2" fontWeight={700} sx={{ color: bizDetailStorePublic ? '#2e7d32' : 'text.secondary' }}>
                                                                    Public Store
                                                                </Typography>
                                                                <Chip
                                                                    label={bizDetailStorePublic ? 'ON' : 'OFF'}
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: 700, fontSize: '0.65rem',
                                                                        bgcolor: bizDetailStorePublic ? '#2e7d32' : '#bdbdbd',
                                                                        color: '#fff', height: 18,
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Switch
                                                                checked={bizDetailStorePublic}
                                                                color="success"
                                                                onChange={async (e) => {
                                                                    const val = e.target.checked;
                                                                    setBizDetailStorePublic(val);
                                                                    try {
                                                                        const res = await businessAPI.updateBusiness(selectedBusinessId, { f_public_store_enabled: val });
                                                                        setBusinesses(prev => prev.map(b => b.f_id === selectedBusinessId ? { ...b, ...res.data } : b));
                                                                    } catch {
                                                                        setBizDetailStorePublic(!val);
                                                                    }
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Customers can browse your products and place orders online — without calling you. Orders appear instantly in your Orders tab.
                                                            </Typography>
                                                            {bizDetailStorePublic && (
                                                                <Box sx={{ p: 1.25, bgcolor: '#f1f8e9', border: '1px solid #a5d6a7', borderRadius: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Your store link</Typography>
                                                                    <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-all', mb: 0.75 }}>
                                                                        {window.location.origin}/store/{selectedBusinessId}
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                                                        <Button size="small" variant="outlined" color="success"
                                                                            onClick={() => window.open(`/store/${selectedBusinessId}`, '_blank')}
                                                                            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}>
                                                                            Test
                                                                        </Button>
                                                                        <Button size="small" variant="outlined"
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(`${window.location.origin}/store/${selectedBusinessId}`);
                                                                            }}
                                                                            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}>
                                                                            Copy Link
                                                                        </Button>
                                                                        <Button size="small" variant="contained"
                                                                            onClick={() => {
                                                                                const url = `${window.location.origin}/store/${selectedBusinessId}`;
                                                                                const msg = encodeURIComponent(`Hi! You can now place orders with us online 🛒\n${url}`);
                                                                                window.open(`https://wa.me/?text=${msg}`, '_blank');
                                                                            }}
                                                                            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe57' } }}>
                                                                            <WhatsAppIcon sx={{ fontSize: 14, mr: 0.5 }} /> Share
                                                                        </Button>
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Paper>

                                                    {/* ── Online Store Orders card ── */}
                                                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                        <Box sx={{ px: 2, py: 1.25, bgcolor: '#f3f4f6', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <OrdersIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                                            <Typography variant="body2" fontWeight={700} color="primary.main">Online Store Orders</Typography>
                                                        </Box>
                                                        <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                Defaults for orders customers place themselves — on your online store or the customer app.
                                                            </Typography>
                                                            <Divider />
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 0.5, letterSpacing: 0 }}>New order starts as</Typography>
                                                                <Typography variant="body2" fontWeight={600}>Created</Typography>
                                                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                                                                    Online orders always come in as Created, so you can review and accept them.
                                                                </Typography>
                                                            </Box>
                                                            <Divider />
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 0.75, letterSpacing: 0 }}>Payment tracked as</Typography>
                                                                <ToggleButtonGroup size="small" exclusive
                                                                    value={osDefaultPaymentStatus}
                                                                    onChange={(_, v) => v && setOsDefaultPaymentStatus(v)}
                                                                    sx={{ flexWrap: 'wrap', gap: 0.75, '& .MuiToggleButtonGroup-grouped': { borderRadius: '6px !important', border: '1.5px solid !important', borderColor: 'divider !important', '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', borderColor: 'primary.main !important' } } }}
                                                                >
                                                                    <ToggleButton value="due" sx={{ px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500 }}>Due</ToggleButton>
                                                                    {osBillingMode.includes('pay_later') && (
                                                                        <ToggleButton value="pay_later" sx={{ px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500 }}>Move to Dues</ToggleButton>
                                                                    )}
                                                                    {osBillingMode.includes('monthly') && (
                                                                        <ToggleButton value="add_to_monthly_bill" sx={{ px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500 }}>Monthly Bill</ToggleButton>
                                                                    )}
                                                                </ToggleButtonGroup>
                                                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                                                                    {{
                                                                        due: 'Payment is immediately due — customer owes for this order right away',
                                                                        pay_later: 'Tracked in the Dues page — multiple unpaid orders are clubbed into a single dues amount per customer',
                                                                        add_to_monthly_bill: 'Added to the customer\'s monthly bill — collected at end of month',
                                                                    }[osDefaultPaymentStatus]}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Paper>

                                                    {/* ── Delivery card ── */}
                                                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                        <Box sx={{ px: 2, py: 1.25, bgcolor: '#f3f4f6', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LocalOfferIcon sx={{ fontSize: 16, color: 'warning.dark' }} />
                                                            <Typography variant="body2" fontWeight={700} sx={{ color: 'warning.dark' }}>Delivery</Typography>
                                                        </Box>
                                                        <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                Applies to online store &amp; customer app orders.
                                                            </Typography>
                                                            <Divider />
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 0.75, letterSpacing: 0 }}>How orders are delivered</Typography>
                                                                <ToggleButtonGroup size="small" exclusive
                                                                    value={osFulfillmentMode}
                                                                    onChange={(_, v) => v && setOsFulfillmentMode(v)}
                                                                    sx={{ '& .MuiToggleButton-root': { px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, borderRadius: '6px !important', border: '1.5px solid !important', borderColor: 'divider !important', mr: 0.75, '&.Mui-selected': { bgcolor: 'warning.dark', color: '#fff', borderColor: '#e65100 !important' } } }}
                                                                >
                                                                    <ToggleButton value="delivery">Home Delivery</ToggleButton>
                                                                    <ToggleButton value="pickup">Self Pickup</ToggleButton>
                                                                    <ToggleButton value="both">Both</ToggleButton>
                                                                </ToggleButtonGroup>
                                                            </Box>
                                                            {osFulfillmentMode !== 'pickup' && (
                                                                <>
                                                                    <Divider />
                                                                    <Box>
                                                                        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 0.75, letterSpacing: 0 }}>Default delivery charge</Typography>
                                                                        <TextField
                                                                            value={osDeliveryCharge}
                                                                            onChange={e => setOsDeliveryCharge(e.target.value)}
                                                                            size="small" type="number"
                                                                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                                                            inputProps={{ min: 0, step: 1, inputMode: 'decimal', pattern: '[0-9]*' }}
                                                                            helperText="Can be overridden per order"
                                                                            sx={{ maxWidth: 160 }}
                                                                        />
                                                                    </Box>
                                                                </>
                                                            )}
                                                        </Box>
                                                    </Paper>
                                                    </>)}

                                                    {orderSettingsView === 'vendor' && (<>
                                                    {/* ── Vendor Orders card (orders you create) ── */}
                                                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                        <Box sx={{ px: 2, py: 1.25, bgcolor: '#eef2ff', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <OrdersIcon sx={{ fontSize: 16, color: 'primary.dark' }} />
                                                            <Typography variant="body2" fontWeight={700} sx={{ color: 'primary.dark' }}>Orders You Create</Typography>
                                                        </Box>
                                                        <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                Defaults used when you or your staff add an order from the Orders page. You can still change them on each order.
                                                            </Typography>
                                                            <Divider />
                                                            {/* Status */}
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 0.75, letterSpacing: 0 }}>New order starts as</Typography>
                                                                <ToggleButtonGroup size="small" exclusive
                                                                    value={osVendorOrderStatus}
                                                                    onChange={(_, v) => v && setOsVendorOrderStatus(v)}
                                                                    sx={{ flexWrap: 'wrap', gap: 0.75, '& .MuiToggleButtonGroup-grouped': { px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, borderRadius: '6px !important', border: '1.5px solid !important', borderColor: 'divider !important', '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', borderColor: 'primary.main !important' } } }}
                                                                >
                                                                    <ToggleButton value="created">Created</ToggleButton>
                                                                    <ToggleButton value="accepted">Accepted</ToggleButton>
                                                                    <ToggleButton value="delivered">Delivered</ToggleButton>
                                                                </ToggleButtonGroup>
                                                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                                                                    {{
                                                                        created:   'Starts unconfirmed — you accept it later',
                                                                        accepted:  'Confirmed as soon as you create it',
                                                                        delivered: 'Marked delivered the moment you create it — for instant sales',
                                                                    }[osVendorOrderStatus]}
                                                                </Typography>
                                                            </Box>
                                                            <Divider />
                                                            {/* Payment */}
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 0.75, letterSpacing: 0 }}>Payment tracked as</Typography>
                                                                <ToggleButtonGroup size="small" exclusive
                                                                    value={osVendorPaymentStatus === 'paid_directly' ? `paid:${osVendorPaymentMethod}` : osVendorPaymentStatus}
                                                                    onChange={(_, v) => {
                                                                        if (!v) return;
                                                                        if (v.startsWith('paid:')) { setOsVendorPaymentStatus('paid_directly'); setOsVendorPaymentMethod(v.slice(5)); }
                                                                        else setOsVendorPaymentStatus(v);
                                                                    }}
                                                                    sx={{ flexWrap: 'wrap', gap: 0.75, '& .MuiToggleButtonGroup-grouped': { borderRadius: '6px !important', border: '1.5px solid !important', borderColor: 'divider !important', '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', borderColor: 'primary.main !important' } } }}
                                                                >
                                                                    <ToggleButton value="due" sx={{ px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500 }}>Due</ToggleButton>
                                                                    <ToggleButton value="paid:UPI" sx={{ px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, '&.Mui-selected': { bgcolor: 'success.main !important', borderColor: 'success.main !important' } }}>Paid — UPI</ToggleButton>
                                                                    <ToggleButton value="paid:Cash" sx={{ px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, '&.Mui-selected': { bgcolor: 'success.main !important', borderColor: 'success.main !important' } }}>Paid — Cash</ToggleButton>
                                                                    <ToggleButton value="paid:Other" sx={{ px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, '&.Mui-selected': { bgcolor: 'success.main !important', borderColor: 'success.main !important' } }}>Paid — Other</ToggleButton>
                                                                    {osBillingMode.includes('pay_later') && (
                                                                        <ToggleButton value="pay_later" sx={{ px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500 }}>Move to Dues</ToggleButton>
                                                                    )}
                                                                    {osBillingMode.includes('monthly') && (
                                                                        <ToggleButton value="add_to_monthly_bill" sx={{ px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500 }}>Monthly Bill</ToggleButton>
                                                                    )}
                                                                </ToggleButtonGroup>
                                                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                                                                    {{
                                                                        due: 'Payment is immediately due — customer owes for this order right away',
                                                                        paid_directly: 'Already paid when you create it — for instant, over-the-counter sales',
                                                                        pay_later: 'Tracked in the Dues page — multiple unpaid orders are clubbed into a single dues amount per customer',
                                                                        add_to_monthly_bill: 'Added to the customer\'s monthly bill — collected at end of month',
                                                                    }[osVendorPaymentStatus]}
                                                                </Typography>
                                                            </Box>
                                                            <Divider />
                                                            {/* Delivery type */}
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 0.75, letterSpacing: 0 }}>Delivery type</Typography>
                                                                <ToggleButtonGroup size="small" exclusive
                                                                    value={osVendorFulfillment}
                                                                    onChange={(_, v) => v && setOsVendorFulfillment(v)}
                                                                    sx={{ '& .MuiToggleButton-root': { px: 2, textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, borderRadius: '6px !important', border: '1.5px solid !important', borderColor: 'divider !important', mr: 0.75, '&.Mui-selected': { bgcolor: 'warning.dark', color: '#fff', borderColor: '#e65100 !important' } } }}
                                                                >
                                                                    <ToggleButton value="delivery">Home Delivery</ToggleButton>
                                                                    <ToggleButton value="pickup">Self Pickup</ToggleButton>
                                                                </ToggleButtonGroup>
                                                            </Box>
                                                            {osVendorFulfillment !== 'pickup' && (
                                                                <>
                                                                    <Divider />
                                                                    <Box>
                                                                        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 0.75, letterSpacing: 0 }}>Default delivery charge</Typography>
                                                                        <TextField
                                                                            value={osVendorDeliveryCharge}
                                                                            onChange={e => setOsVendorDeliveryCharge(e.target.value)}
                                                                            size="small" type="number"
                                                                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                                                            inputProps={{ min: 0, step: 1, inputMode: 'decimal', pattern: '[0-9]*' }}
                                                                            helperText="Pre-filled on new orders; change it per order"
                                                                            sx={{ maxWidth: 160 }}
                                                                        />
                                                                    </Box>
                                                                </>
                                                            )}
                                                        </Box>
                                                    </Paper>
                                                    </>)}

                                                    {orderSettingsView === 'common' && (<>
                                                    {/* ── Payment Options card ── */}
                                                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                        <Box sx={{ px: 2, py: 1.25, bgcolor: '#f3f4f6', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Receipt sx={{ fontSize: 16, color: 'primary.dark' }} />
                                                            <Typography variant="body2" fontWeight={700} sx={{ color: 'primary.dark' }}>Payment Options</Typography>
                                                        </Box>
                                                        <Box sx={{ px: 2, py: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                                Extra ways a customer can pay for an order, besides paying now. Turn on only what you use. Applies to online and vendor-created orders.
                                                            </Typography>
                                                            {/* Move to Dues */}
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="body2" fontWeight={600}>Move to Dues</Typography>
                                                                    <Typography variant="caption" color="text.disabled">Unpaid orders are clubbed into a single dues amount per customer, tracked on the Dues page</Typography>
                                                                </Box>
                                                                <Switch
                                                                    checked={osBillingMode.includes('pay_later')}
                                                                    onChange={e => {
                                                                        const dues = e.target.checked;
                                                                        const monthly = osBillingMode.includes('monthly');
                                                                        setOsBillingMode(monthly && dues ? 'monthly+pay_later' : dues ? 'pay_later' : monthly ? 'monthly' : 'none');
                                                                    }}
                                                                />
                                                            </Box>
                                                            {/* Monthly Bills */}
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="body2" fontWeight={600}>Monthly Bills</Typography>
                                                                    <Typography variant="caption" color="text.disabled">Order is added to the customer's monthly bill, collected at end of month. Bills page becomes visible.</Typography>
                                                                </Box>
                                                                <Switch
                                                                    checked={osBillingMode.includes('monthly')}
                                                                    onChange={e => {
                                                                        const monthly = e.target.checked;
                                                                        const dues = osBillingMode.includes('pay_later');
                                                                        setOsBillingMode(monthly && dues ? 'monthly+pay_later' : monthly ? 'monthly' : dues ? 'pay_later' : 'none');
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Paper>

                                                    {/* ── Store Notes card ── */}
                                                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                        <Box sx={{ px: 2, py: 1.25, bgcolor: '#f3f4f6', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Inventory sx={{ fontSize: 16, color: 'success.main' }} />
                                                            <Typography variant="body2" fontWeight={700} sx={{ color: 'success.main' }}>Store Notes</Typography>
                                                        </Box>
                                                        <Box sx={{ px: 2, py: 2 }}>
                                                            <TextField
                                                                placeholder="Prices are indicative and may vary. Your order may be revised by the vendor before confirmation."
                                                                value={osNotes}
                                                                onChange={e => setOsNotes(e.target.value)}
                                                                size="small" fullWidth multiline rows={3}
                                                                inputProps={{ maxLength: 1000 }}
                                                                helperText={`${osNotes.length}/1000 · Shown while placing an order. Left blank, this default note is shown.`}
                                                            />
                                                        </Box>
                                                    </Paper>

                                                    {/* ── Terms & Conditions card ── */}
                                                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                        <Box sx={{ px: 2, py: 1.25, bgcolor: '#f3f4f6', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Receipt sx={{ fontSize: 16, color: 'primary.dark' }} />
                                                            <Typography variant="body2" fontWeight={700} sx={{ color: 'primary.dark' }}>Terms &amp; Conditions</Typography>
                                                        </Box>
                                                        <Box sx={{ px: 2, py: 2 }}>
                                                            <TextField
                                                                placeholder="e.g. All sales are final. Delivery timings may vary. Contact us for any queries."
                                                                value={osTerms}
                                                                onChange={e => setOsTerms(e.target.value)}
                                                                size="small" fullWidth multiline rows={4}
                                                                inputProps={{ maxLength: 2000 }}
                                                                helperText={`${osTerms.length}/2000 · Will appear on order invoices`}
                                                            />
                                                        </Box>
                                                    </Paper>

                                                    {/* ── Opening Hours card ── */}
                                                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                        <Box
                                                            onClick={() => setOsHoursExpanded(v => !v)}
                                                            sx={{ px: 2, py: 1.25, bgcolor: '#f3f4f6', borderBottom: osHoursExpanded ? '1px solid' : 'none', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <TodayIcon sx={{ fontSize: 16, color: '#7b1fa2' }} />
                                                                <Typography variant="body2" fontWeight={700} sx={{ color: '#7b1fa2' }}>Opening Hours</Typography>
                                                            </Box>
                                                            <Typography variant="caption" sx={{ color: '#7b1fa2', fontWeight: 600 }}>{osHoursExpanded ? '▾ Hide' : '▸ Set hours'}</Typography>
                                                        </Box>
                                                        {osHoursExpanded && osHours && (
                                                            <Box sx={{ px: 2, py: 1.5 }}>
                                                                {['mon','tue','wed','thu','fri','sat','sun'].map(day => (
                                                                    <Box key={day} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                                                                        <Typography variant="body2" fontWeight={500} sx={{ width: 36, color: osHours[day]?.closed ? 'text.disabled' : 'text.primary', flexShrink: 0 }}>
                                                                            {day.charAt(0).toUpperCase() + day.slice(1)}
                                                                        </Typography>
                                                                        <Switch size="small"
                                                                            checked={!osHours[day]?.closed}
                                                                            onChange={e => setOsHours(prev => ({ ...prev, [day]: { ...prev[day], closed: !e.target.checked } }))}
                                                                        />
                                                                        {!osHours[day]?.closed ? (
                                                                            <>
                                                                                <TextField size="small" type="time"
                                                                                    value={osHours[day]?.open || '09:00'}
                                                                                    onChange={e => setOsHours(prev => ({ ...prev, [day]: { ...prev[day], open: e.target.value } }))}
                                                                                    InputLabelProps={{ shrink: true }}
                                                                                    inputProps={{ step: 900 }}
                                                                                    sx={{ width: 110 }}
                                                                                />
                                                                                <Typography variant="body2" color="text.disabled">–</Typography>
                                                                                <TextField size="small" type="time"
                                                                                    value={osHours[day]?.close || '18:00'}
                                                                                    onChange={e => setOsHours(prev => ({ ...prev, [day]: { ...prev[day], close: e.target.value } }))}
                                                                                    InputLabelProps={{ shrink: true }}
                                                                                    inputProps={{ step: 900 }}
                                                                                    sx={{ width: 110 }}
                                                                                />
                                                                            </>
                                                                        ) : (
                                                                            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Closed</Typography>
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Paper>
                                                    </>)}

                                                    <Button variant="contained" onClick={handleOrderSettingsSave}
                                                        disabled={orderSettingsSaving} disableElevation
                                                        sx={{ textTransform: 'none', fontWeight: 600, py: 1.25, borderRadius: 2 }}>
                                                        {orderSettingsSaving ? <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" /> : null}
                                                        Save Order Settings
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    )}

                                    {/* Business sub-tab */}
                                    {settingsSubTab === 3 && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Update your business details. These appear on invoices.
                                            </Typography>

                                            {bizDetailError && (
                                                <Alert severity="error" onClose={() => setBizDetailError('')}>{bizDetailError}</Alert>
                                            )}
                                            {bizDetailSuccess && (
                                                <Alert severity="success" onClose={() => setBizDetailSuccess('')}>{bizDetailSuccess}</Alert>
                                            )}

                                            {/* Business Logo */}
                                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                                                {bizDetailLogoUrl ? (
                                                    <Box component="img" src={bizDetailLogoUrl} alt="logo"
                                                        onClick={() => setPreviewImg(bizDetailLogoUrl)}
                                                        sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0, bgcolor: '#f5f5f5', cursor: 'pointer' }} />
                                                ) : (
                                                    <Box sx={{ width: 72, height: 72, borderRadius: 1.5, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <PhotoCamera sx={{ color: '#bdbdbd', fontSize: 28 }} />
                                                    </Box>
                                                )}
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>Business Logo</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                        {bizDetailLogoUrl ? 'Tap to preview · Change or Remove below' : 'Square image works best'}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <Button component="label" size="small" variant="outlined" disabled={logoUploading}
                                                            startIcon={logoUploading ? <CircularProgress size={14} /> : <PhotoCamera sx={{ fontSize: '0.9rem !important' }} />}
                                                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                                            {bizDetailLogoUrl ? 'Change' : 'Add Logo'}
                                                            <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
                                                        </Button>
                                                        {bizDetailLogoUrl && (
                                                            <IconButton size="small" onClick={handleLogoRemove} disabled={logoUploading} sx={{ color: 'error.main' }}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                    {logoError && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{logoError}</Typography>}
                                                </Box>
                                            </Box>

                                            <TextField
                                                label="Business Name"
                                                value={bizDetailName}
                                                onChange={e => setBizDetailName(e.target.value)}
                                                size="small" fullWidth
                                                required
                                            />
                                            <TextField
                                                label="GST Number"
                                                value={bizDetailGst}
                                                onChange={e => setBizDetailGst(e.target.value)}
                                                size="small" fullWidth
                                                placeholder="e.g. 22AAAAA0000A1Z5"
                                                inputProps={{ maxLength: 15 }}
                                            />
                                            <TextField
                                                label="UPI ID"
                                                value={bizDetailUpi}
                                                onChange={e => setBizDetailUpi(e.target.value)}
                                                size="small" fullWidth
                                                placeholder="e.g. business@upi"
                                            />
                                            <TextField
                                                label="Phone"
                                                value={bizDetailPhone}
                                                onChange={e => setBizDetailPhone(e.target.value)}
                                                size="small" fullWidth
                                                inputProps={{ maxLength: 15 }}
                                            />
                                            <TextField
                                                label="Address"
                                                value={bizDetailAddress1}
                                                onChange={e => setBizDetailAddress1(e.target.value)}
                                                size="small" fullWidth multiline rows={2}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <TextField
                                                    label="City"
                                                    value={bizDetailCity}
                                                    onChange={e => setBizDetailCity(e.target.value)}
                                                    size="small" sx={{ flex: 1 }}
                                                />
                                                <TextField
                                                    label="State"
                                                    value={bizDetailState}
                                                    onChange={e => setBizDetailState(e.target.value)}
                                                    size="small" sx={{ flex: 1 }}
                                                />
                                                <TextField
                                                    label="PIN"
                                                    value={bizDetailPin}
                                                    onChange={e => setBizDetailPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                    size="small" sx={{ width: 90 }}
                                                    inputProps={{ inputMode: 'numeric' }}
                                                />
                                            </Box>

                                            <Box>
                                                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>Business Type</Typography>
                                                <Box sx={{ display: 'flex', gap: 0.75 }}>
                                                    {[
                                                        { value: 'subscription', emoji: '📦', title: 'Regular Delivery/Plans' },
                                                        { value: 'orders', emoji: '🛍️', title: 'Orders' },
                                                        { value: 'both', emoji: '📦🛍️', title: 'Both' },
                                                    ].map(opt => {
                                                        const selected = bizDetailMode === opt.value;
                                                        return (
                                                            <Paper key={opt.value} variant="outlined"
                                                                onClick={() => setBizDetailMode(opt.value)}
                                                                sx={{
                                                                    flex: 1, minWidth: 0, p: 1, cursor: 'pointer', borderWidth: 2, borderRadius: 1.5,
                                                                    borderColor: selected ? 'primary.main' : 'divider',
                                                                    bgcolor: selected ? 'primary.50' : 'background.paper',
                                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, textAlign: 'center',
                                                                }}>
                                                                <Typography sx={{ fontSize: '1.3rem', lineHeight: 1 }}>{opt.emoji}</Typography>
                                                                <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.15 }}
                                                                    color={selected ? 'primary.main' : 'text.primary'}>{opt.title}</Typography>
                                                            </Paper>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>

                                            {/* Public Store toggle hidden here — managed in Order Settings instead */}

                                            <Button variant="contained" onClick={handleBizDetailSave}
                                                disabled={bizDetailLoading || !bizDetailName.trim()}
                                                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                                                {bizDetailLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                                                Save Changes
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Orders Tab */}
                            {activeTab === 2 && showOrders && (
                                <Box>
                                    <Tabs
                                        value={ordersSubTab}
                                        onChange={(_, v) => setOrdersSubTab(v)}
                                        variant="fullWidth"
                                        sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
                                    >
                                        <Tab label="Active" value={0} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
                                        <Tab label="Closed" value={1} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
                                        <Tab label="Reports" value={2} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
                                        <Tab label={`Alerts${orderAlertCount ? ` (${orderAlertCount})` : ''}`} value={3} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
                                    </Tabs>
                                    {ordersSubTab === 0 && (
                                        <OrdersPage
                                            businessId={selectedBusinessId}
                                            business={selectedBusiness}
                                            customers={businessCustomers}
                                            products={businessProducts}
                                            members={businessMembers}
                                            orderSettings={orderSettingsData}
                                            onCustomerCreated={handleCustomersUpdated}
                                            initialScope="open"
                                        />
                                    )}
                                    {ordersSubTab === 1 && (
                                        <OrdersPage
                                            businessId={selectedBusinessId}
                                            business={selectedBusiness}
                                            customers={businessCustomers}
                                            products={businessProducts}
                                            members={businessMembers}
                                            orderSettings={orderSettingsData}
                                            onCustomerCreated={handleCustomersUpdated}
                                            initialScope="closed"
                                            onRestored={() => setOrdersSubTab(0)}
                                        />
                                    )}
                                    {ordersSubTab === 2 && (
                                        <OrdersSummaryPage
                                            businessId={selectedBusinessId}
                                            business={selectedBusiness}
                                        />
                                    )}
                                    {ordersSubTab === 3 && (
                                        <OrderPaymentAlerts
                                            businessId={selectedBusinessId}
                                            onCountChange={setOrderAlertCount}
                                        />
                                    )}
                                </Box>
                            )}

                            {/* Overview Tab */}
                            {activeTab === 7 && (
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        {t('dashboard.guideTitle')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {[1,2,3,4,5,6,7,8].map(n => (
                                            <Card key={n} variant="outlined">
                                                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                                                        {t(`dashboard.guideStep${n}Title`)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t(`dashboard.guideStep${n}`)}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                        {t('dashboard.quickActions')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Button variant="outlined" startIcon={<People />} fullWidth onClick={() => setActiveTab(1)}>
                                            {t('dashboard.manageCustomers')}
                                        </Button>
                                        <Button variant="outlined" startIcon={<Inventory />} fullWidth onClick={() => setActiveTab(0)}>
                                            {t('dashboard.manageProducts')}
                                        </Button>
                                        {showDuesTab && (
                                            <Button variant="outlined" startIcon={<CurrencyRupee />} fullWidth onClick={() => setActiveTab(5)}>
                                                {t('dashboard.viewDues')}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            )}
                </Box>
            ) : (
                /* Business exists but none selected */
                <Card elevation={2}>
                    <CardContent>
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <BusinessIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h5" color="text.secondary" gutterBottom>
                                {t('dashboard.selectBusiness')}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {t('dashboard.selectBusinessDesc')}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Business Form Dialog */}
            <Dialog 
                open={showBusinessForm} 
                onClose={() => setShowBusinessForm(false)}
                {...APP_DIALOG}
            >
                <DialogTitle sx={APP_DIALOG_TITLE_SX}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">{t('dashboard.addNewBusiness')}</Typography>
                        <IconButton onClick={() => setShowBusinessForm(false)}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <BusinessForm onBusinessCreated={handleBusinessCreated} defaultPhone={user?.f_mobile || ''} />
                </DialogContent>
            </Dialog>

            {/* Remove agent: their customers go to someone first */}
            <Dialog open={!!memberRemove} onClose={() => !memberRemoving && setMemberRemove(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Remove {memberRemove?.member.f_name || memberRemove?.member.f_mobile}?</DialogTitle>
                <DialogContent>
                    {memberRemove?.count > 0 ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                                <strong>{memberRemove.count} customer{memberRemove.count !== 1 ? 's' : ''}</strong> {memberRemove.count !== 1 ? 'are' : 'is'} with
                                {' '}{memberRemove.member.f_name || 'this agent'}. Who should get them?
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {[...businessMembers.filter(m => m.f_id !== memberRemove.member.f_id)
                                    .map(m => ({ key: m.f_id, label: m.f_role === 'Owner' ? `${m.f_name || m.f_mobile} (owner)` : (m.f_name || m.f_mobile) })),
                                  { key: 'none', label: 'No agent' }].map(o => {
                                    const on = memberRemove.moveTo === o.key;
                                    return (
                                        <Button key={o.key} fullWidth variant={on ? 'contained' : 'outlined'} disableElevation
                                            onClick={() => setMemberRemove(r => ({ ...r, moveTo: o.key }))}
                                            sx={{ justifyContent: 'flex-start', minHeight: 44, textTransform: 'none', fontWeight: on ? 800 : 600 }}>
                                            {on ? '✓ ' : ''}{o.label}
                                        </Button>
                                    );
                                })}
                            </Box>
                            {memberRemove.moveTo != null && (
                                <Alert severity="warning" sx={{ mt: 1.5 }}>
                                    All {memberRemove.count} customer{memberRemove.count !== 1 ? 's' : ''} of {memberRemove.member.f_name || 'this agent'} will
                                    {' '}{memberRemove.moveTo === 'none'
                                        ? 'have no agent'
                                        : `move to ${agentNameOf(memberRemove.moveTo)}`}, and {memberRemove.member.f_name || 'they'} will
                                    {' '}no longer see this business.
                                </Alert>
                            )}
                        </>
                    ) : (
                        <Typography variant="body2">They will no longer be able to see this business.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setMemberRemove(null)} disabled={memberRemoving}>Cancel</Button>
                    <Button variant="contained" color="error" disableElevation onClick={confirmMemberRemove}
                        disabled={memberRemoving || (memberRemove?.count > 0 && memberRemove?.moveTo == null)}
                        startIcon={memberRemoving ? <CircularProgress size={16} color="inherit" /> : null}>
                        {memberRemove?.count > 0 ? 'Move & remove' : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Product Confirm Dialog */}
            <Dialog open={!!archiveProductTarget} onClose={() => !archiveProductLoading && !archiveEndingMode && setArchiveProductTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{archiveProductTarget?.f_parent_business_product_id ? 'Delete plan?' : 'Delete product?'}</DialogTitle>
                <DialogContent>
                    {archiveProductChecking ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">Checking active plans…</Typography>
                        </Box>
                    ) : archiveProductActivePlans > 0 ? (() => {
                        // Customers are still on it. Deleting alone would hide a plan that keeps
                        // billing, so their plans end first. Two ways, because a deleted product
                        // also drops off this month's bill if it is made again.
                        const n = archiveProductActivePlans;
                        const names = archiveProductHolderIds
                            .map(id => businessCustomers.find(c => c.f_id === id)?.f_name).filter(Boolean);
                        // "September and October 2026" — the bill months still open.
                        const months = archiveOpenMonths;
                        const monthsText = months.length <= 1 ? (months[0] || '')
                            : `${months.slice(0, -1).join(', ')} and ${months[months.length - 1]}`;
                        const billsWord = months.length > 1 ? 'bills' : 'bill';
                        const busy = !!archiveEndingMode;
                        const option = (mode, title, note, color) => (
                            <Button fullWidth variant={mode === 'end' ? 'contained' : 'outlined'} color={color}
                                disableElevation disabled={busy} onClick={() => handleEndAllPlans(mode === 'end_delete')}
                                sx={{ display: 'block', textAlign: 'left', textTransform: 'none', px: 1.5, py: 1.25, mb: 1, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {archiveEndingMode === mode && <CircularProgress size={16} color="inherit" />}
                                    <Typography variant="body2" fontWeight={800}>{title}</Typography>
                                </Box>
                                <Typography variant="caption" sx={{ display: 'block', opacity: 0.85, lineHeight: 1.35, mt: 0.25 }}>{note}</Typography>
                            </Button>
                        );
                        return (
                            <>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>{n} customer{n !== 1 ? 's are' : ' is'}</strong> still getting <strong>{archiveProductTarget?.f_name}</strong>.
                                    {' '}Their plans need to end first.
                                </Typography>
                                {names.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                        {names.slice(0, 4).join(', ')}{names.length > 4 ? ` and ${names.length - 4} more` : ''}
                                    </Typography>
                                )}
                                {months.length > 0 ? (
                                    <>
                                        {option('end', 'End plans today, delete later',
                                            `Last delivery is today. Deliveries in ${monthsText} stay on the ${billsWord}. Delete it after ${monthsText} ${months.length > 1 ? 'are' : 'is'} closed.`,
                                            'primary')}
                                        {option('end_delete', 'End plans and delete now',
                                            `Deliveries of ${archiveProductTarget?.f_name} in ${monthsText} will not be on the ${billsWord} if ${billsWord} ${months.length > 1 ? 'are' : 'is'} made again.`,
                                            'error')}
                                    </>
                                ) : (
                                    // Every bill month is closed: nothing left to lose by deleting now.
                                    option('end_delete', 'End plans and delete',
                                        'Last delivery is today. All bills are closed, so no deliveries are lost.',
                                        'error')
                                )}
                            </>
                        );
                    })() : (
                        archiveProductTarget?.f_parent_business_product_id ? (
                            <Typography variant="body2">
                                Delete the plan <strong>{archiveProductTarget?.f_name}</strong>? It can't be brought back — you would have to make it again.
                            </Typography>
                        ) : (
                            <Typography variant="body2">
                                Delete <strong>{archiveProductTarget?.f_name}</strong>? It will be removed from the product list and won't appear for new plans.
                                You can bring it back later from Settings → Deleted.
                            </Typography>
                        )
                    )}
                    {archiveProductError && <Alert severity="error" sx={{ mt: 1.5 }}>{archiveProductError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setArchiveProductTarget(null)} disabled={archiveProductLoading || !!archiveEndingMode}>
                        Cancel
                    </Button>
                    {archiveProductActivePlans > 0 ? null : (
                        <Button
                            variant="contained" color="error"
                            onClick={handleArchiveProductConfirm}
                            disabled={archiveProductLoading || archiveProductChecking}
                            startIcon={archiveProductLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Delete
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar open={!!deleteNotice} autoHideDuration={5000} onClose={() => setDeleteNotice('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                // Above the bottom bar, not under it.
                sx={{ bottom: { xs: 'calc(80px + env(safe-area-inset-bottom, 0px))' } }}>
                <Alert severity="success" variant="filled" onClose={() => setDeleteNotice('')} sx={{ width: '100%' }}>
                    {deleteNotice}
                </Alert>
            </Snackbar>

            <ShareProductsDialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                businessId={selectedBusinessId}
                productCount={businessProducts.filter(p => !p.f_parent_business_product_id && !p.f_is_delivery_product).length}
            />
            <SharedProductsDialog
                open={!!openShare}
                share={openShare}
                businessId={selectedBusinessId}
                onClose={() => setOpenShare(null)}
                onAdded={(res) => {
                    handleProductsUpdated();
                    loadPendingShares();
                    const n = res?.added || 0;
                    const skipped = res?.skipped?.length || 0;
                    setDeleteNotice(`Added ${n} product${n !== 1 ? 's' : ''}.${skipped ? ` ${skipped} skipped — you already have them.` : ''}`);
                }}
            />

            {/* Product Edit Dialog */}
            <ProductFormDialog
                open={!!editingProduct}
                onClose={() => setEditingProduct(null)}
                mode="edit"
                // Fresh copy from the list, so a price changed meanwhile shows here too.
                product={businessProducts.find(p => p.f_id === editingProduct?.f_id) || editingProduct}
                bizMode={bizMode}
                categoryOptions={orderedCategoryOptions}
                onSaved={handleProductsUpdated}
                onChangePrice={(p) => setPriceDialogProduct(p)}
            />

            {/* Plan create/edit — reached from the product it belongs to, in the Products list */}
            <PlanFormDialog
                open={!!planForm}
                businessId={selectedBusinessId}
                product={planForm?.product}
                plan={planForm?.plan}
                onClose={(saved) => { setPlanForm(null); if (saved) handleProductsUpdated(); }}
            />

            {/* Price Dialog */}
            <ProductPriceDialog
                open={!!priceDialogProduct}
                onClose={() => setPriceDialogProduct(null)}
                product={priceDialogProduct}
                onProductUpdated={handleProductsUpdated}
            />

            {/* Discount Dialog */}
            <ProductDiscountDialog
                open={!!discountDialogProduct}
                onClose={() => setDiscountDialogProduct(null)}
                product={discountDialogProduct}
                onProductUpdated={handleProductsUpdated}
            />

            {/* Category Manager */}
            <ProductCategoryManager
                open={categoryManagerOpen}
                onClose={() => { setCategoryManagerOpen(false); loadProductCategoryOrder(selectedBusiness?.f_id); }}
                businessId={selectedBusiness?.f_id}
                businessProducts={businessProducts}
                onProductsUpdated={handleProductsUpdated}
            />

            <CataloguePicker
                open={cataloguePickerOpen}
                onClose={() => setCataloguePickerOpen(false)}
                businessId={selectedBusiness?.f_id}
                existingProductIds={businessProducts.map(p => p.f_product_id).filter(Boolean)}
                onAdded={handleProductsUpdated}
            />

            {/* Bulk Price Update */}
            <BulkPriceDialog
                open={bulkPriceOpen}
                onClose={() => setBulkPriceOpen(false)}
                businessId={selectedBusiness?.f_id}
                businessProducts={businessProducts}
                onProductsUpdated={handleProductsUpdated}
            />
        </Container>
        {/* Settings FABs */}
        {activeTab === 0 && settingsSubTab === 0 && selectedBusiness && (
            <Fab color="primary" variant="extended" size="medium"
                onClick={() => setQuickProductOpen(true)}
                sx={{ position: 'fixed', bottom: barsVisible ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : 'calc(16px + env(safe-area-inset-bottom, 0px))', right: 'max(16px, calc(50% - 344px))', zIndex: 1050, gap: 0.75, px: 2 }}>
                <Add /> Add Product
            </Fab>
        )}
        {activeTab === 0 && settingsSubTab === 1 && selectedBusiness && (
            <Fab color="primary" variant="extended" size="medium"
                onClick={() => { setAreaShowAddForm(true); setAreaEditingId(null); setAreaOpsError(''); }}
                sx={{ position: 'fixed', bottom: barsVisible ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : 'calc(16px + env(safe-area-inset-bottom, 0px))', right: 'max(16px, calc(50% - 344px))', zIndex: 1050, gap: 0.75, px: 2 }}>
                <Add /> Add Area
            </Fab>
        )}
        {activeTab === 0 && settingsSubTab === 2 && selectedBusiness && selectedBusiness?.f_created_by === user?.f_id && (
            <Fab color="primary" variant="extended" size="medium"
                onClick={() => { setMemberShowAddForm(true); setMemberLookupResult(null); setMemberLookupError(''); setMemberMobileInput(''); setMemberNameInput(''); setMemberSelectedRole('Agent'); setMemberOpsError(''); setMemberOpsSuccess(''); }}
                sx={{ position: 'fixed', bottom: barsVisible ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : 'calc(16px + env(safe-area-inset-bottom, 0px))', right: 'max(16px, calc(50% - 344px))', zIndex: 1050, gap: 0.75, px: 2 }}>
                <Add /> Add Agent
            </Fab>
        )}
        {selectedBusiness && (
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    minHeight: 64,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                    opacity: barsVisible && !customerDrawerOpen ? 1 : 0,
                    visibility: barsVisible && !customerDrawerOpen ? 'visible' : 'hidden',
                    pointerEvents: barsVisible && !customerDrawerOpen ? 'auto' : 'none',
                    transition: 'opacity 0.25s ease, visibility 0.25s ease',
                    willChange: 'opacity',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}
            >
                <BottomNavigation
                    value={activeTab}
                    onChange={(_, v) => { setActiveTab(v); setBarsVisible(true); }}
                    showLabels
                    sx={{
                        height: 64,
                        width: '100%',
                        maxWidth: 720,
                        mx: 'auto',
                        '& .MuiBottomNavigationAction-root': {
                            minWidth: 0,
                            flex: 1,
                            px: 0.5,
                        },
                    }}
                >
                    <BottomNavigationAction label={t('nav.settings')} icon={
                        <Badge color="warning" badgeContent={shareAlertCount} max={99}>
                            <Inventory />
                        </Badge>
                    } />
                    <BottomNavigationAction label={t('nav.customers')} icon={<People />} />
                    <BottomNavigationAction label="Orders" icon={<OrdersIcon />} sx={{ display: showOrders ? 'flex' : 'none' }} />
                    <BottomNavigationAction label={t('nav.subscriptions')} icon={
                        <Badge color="warning" badgeContent={planAlertCount} max={99}>
                            <AutorenewIcon />
                        </Badge>
                    } sx={{ display: showSubscription ? 'flex' : 'none' }} />
                    <BottomNavigationAction label={t('nav.billing')} icon={<Receipt />} sx={{ display: showBilling ? 'flex' : 'none' }} />
                    <BottomNavigationAction label={t('nav.dues')} icon={
                        <Badge color="warning" badgeContent={duesAlertCount} max={99}>
                            <CurrencyRupee />
                        </Badge>
                    } sx={{ display: showDuesTab ? 'flex' : 'none' }} />
                    <BottomNavigationAction label={t('nav.daily')} icon={<TodayIcon />} sx={{ display: showSubscription ? 'flex' : 'none' }} />
                    <BottomNavigationAction label={t('nav.overview')} icon={<DashboardIcon />} sx={{ display: 'none' }} />
                </BottomNavigation>
            </Box>
        )}
        </>
        )}

        {/* Delivery order within one area (or the customers with no area) */}
        <DeliveryOrderDialog
            open={routeOrderArea !== null}
            onClose={(changed) => {
                setRouteOrderArea(null);
                // Ranks moved on the server; refresh so the next open starts from the truth.
                if (changed && selectedBusinessId) loadBusinessCustomers(selectedBusinessId);
            }}
            businessId={selectedBusinessId}
            title={routeOrderArea?.name || ''}
            customers={routeOrderCustomers}
        />

        {/* Area → Customer Assignment Dialog — same layout as the product Add Customers dialog */}
        <AssignCustomersDialog
            open={areaAssignId !== null}
            title={`Add customers — ${businessAreas.find(a => a.f_id === areaAssignId)?.f_name || ''}`}
            customers={businessCustomers.filter(c => !c.f_is_archived)}
            isMember={c => c.f_area_id === areaAssignId}
            groupKeyOf={c => c.f_area_id || null}
            groupNameOf={c => (c.f_area_id
                ? (businessAreas.find(a => a.f_id === c.f_area_id)?.f_name || 'Other area')
                : null)}
            agentOf={c => agentNameOf(c.f_agent_id)}
            areaRankOf={areaRankOf}
            labels={{ group: 'area', none: 'No area', other: 'Other areas' }}
            onAdd={ids => assignCustomersTo('f_area_id', areaAssignId, ids, setAreaOpsError)}
            onRemove={id => assignCustomersTo('f_area_id', null, [id], setAreaOpsError)}
            onClose={() => setAreaAssignId(null)}
        />

        {/* Edit an agent's name / phone number.
            Changing the number moves who this agent *is*: whoever was logged in on the old
            number loses this business, and the new number's account gains it. The agent's
            customers and orders hang off the row, so they are unaffected either way. */}
        {/* Compact rather than the full-page APP_DIALOG: two fields do not fill a phone screen. */}
        <Dialog open={!!memberEditTarget} onClose={() => setMemberEditTarget(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Change agent details</DialogTitle>
            <DialogContent>
                {memberEditError && <Alert severity="error" sx={{ mb: 1.5 }}>{memberEditError}</Alert>}
                <TextField label="Agent name" value={memberEditName}
                    onChange={e => setMemberEditName(e.target.value)}
                    size="small" fullWidth sx={{ mt: 1, mb: 2 }} />
                <TextField label="Phone number" value={memberEditMobile}
                    onChange={e => setMemberEditMobile(e.target.value)}
                    size="small" fullWidth type="tel"
                    inputProps={{ inputMode: 'numeric', maxLength: 10 }} />
                {memberEditMobile.trim().length === 10 && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, letterSpacing: 1 }}>
                        {memberEditMobile.trim().replace(/(\d{5})(\d{5})/, '$1 $2')}
                    </Typography>
                )}
                {memberEditTarget && memberEditMobile.trim() !== (memberEditTarget.f_mobile || '') && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {memberEditTarget.f_user_id
                            ? 'The old number will no longer be able to open this shop. Their customers and orders stay with this agent.'
                            : 'Their customers and orders stay with this agent.'}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setMemberEditTarget(null)} sx={{ minHeight: TOUCH.min }}>Cancel</Button>
                <Button variant="contained" onClick={handleMemberEditSave}
                    disabled={memberEditSaving || memberEditMobile.trim().length < 10}
                    sx={{ minHeight: TOUCH.min }}>
                    {memberEditSaving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Agent → Customer Assignment Dialog — same layout, with area chips to pick a round */}
        <AssignCustomersDialog
            open={agentAssignId !== null}
            title={`Add customers — ${(() => { const m = businessMembers.find(x => x.f_id === agentAssignId); return m ? (m.f_name || m.f_mobile) : ''; })()}`}
            customers={businessCustomers.filter(c => !c.f_is_archived)}
            isMember={c => c.f_agent_id === agentAssignId}
            groupKeyOf={c => c.f_agent_id || null}
            groupNameOf={c => agentNameOf(c.f_agent_id)}
            areaOf={c => businessAreas.find(a => a.f_id === c.f_area_id)?.f_name || null}
            areaRankOf={areaRankOf}
            labels={{ group: 'agent', none: 'No agent', other: 'Other agents' }}
            onAdd={ids => assignCustomersTo('f_agent_id', agentAssignId, ids, setMemberOpsError)}
            onRemove={id => assignCustomersTo('f_agent_id', null, [id], setMemberOpsError)}
            onClose={() => setAgentAssignId(null)}
        />

        {/* Product → Bulk Add Customer Plans Dialog */}
        {(() => {
            const planProduct = businessProducts.find(p => p.f_id === planProductId);
            const closePlanDialog = () => { setPlanProductId(null); setPlanSelectedIds([]); setPlanCustomerQty({}); setPlanAddResult(null); setPlanFilterBy(null); setPlanAreaFilter(''); setPlanAllSubCustIds(new Set()); setPlanAssignedExpanded(false); setPlanAssignedPage(50); };
            const searchLower = planProductSearch.toLowerCase();

            // Customers WITHOUT this product's active plan (eligible to add), with the plan chips applied
            const eligible = businessCustomers.filter(c => {
                if (c.f_is_archived || planProductSubs.includes(c.f_id)) return false;
                if (planFilterBy === 'no_plans' && planAllSubCustIds.has(c.f_id)) return false;
                if (planFilterBy === 'other_products' && !planAllSubCustIds.has(c.f_id)) return false;
                return true;
            });
            // Area counts come from that list, so each chip's number is what tapping it shows.
            const areaOptions = sortAreasByRoute(businessAreas)
                .map(a => ({ key: a.f_id, label: a.f_name, count: eligible.filter(c => c.f_area_id === a.f_id).length }))
                .filter(a => a.count > 0);
            const unassigned = eligible.filter(c => {
                if (planAreaFilter === PLAN_NO_AREA ? c.f_area_id : (planAreaFilter && c.f_area_id !== planAreaFilter)) return false;
                return !searchLower || c.f_name.toLowerCase().includes(searchLower) || c.f_mobile.includes(searchLower);
            });

            const sortedUnassigned = sortCustomers(unassigned, planSort, { areaRankOf, agentOf: c => agentNameOf(c.f_agent_id) });

            const visibleUnassigned = sortedUnassigned.slice(0, planProductPage);
            const hasMore = sortedUnassigned.length > planProductPage;
            const allUnassignedIds = unassigned.map(c => c.f_id);
            const allSelected = allUnassignedIds.length > 0 && allUnassignedIds.every(id => planSelectedIds.includes(id));

            const assignedCustomers = businessCustomers.filter(c => planProductSubs.includes(c.f_id));
            const visibleAssigned = assignedCustomers.slice(0, planAssignedPage);
            const hasMoreAssigned = assignedCustomers.length > planAssignedPage;
            return (
                <Dialog open={planProductId !== null} onClose={!planAdding ? closePlanDialog : undefined}
                        disableScrollLock={false} {...APP_DIALOG}>
                    <DialogTitle sx={APP_DIALOG_TITLE_SX}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, pr: 1, minWidth: 0 }}>
                                <Typography variant="h6" noWrap>
                                    Add Customers — {planProduct?.f_name || ''}
                                </Typography>
                                {/* Which days these customers will actually receive it */}
                                {planProduct?.schedule_label && planProduct.schedule_label !== 'Every day' && (
                                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                        {planProduct.schedule_label}
                                    </Typography>
                                )}
                            </Box>
                            <IconButton onClick={closePlanDialog} disabled={planAdding} sx={{ color: 'white' }}><Close /></IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers sx={{ p: 0 }}>
                        {/* Sticky header: start date, search, sort */}
                        <Box sx={{ px: 2, pt: 1.5, pb: 1.5, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                                <TextField
                                    label="Start From" type="date" size="small"
                                    value={planStartDate}
                                    onChange={e => setPlanStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                            {/* Search ~2/3, Sort by the rest — as on the Customers page */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <TextField
                                    size="small" placeholder="Search by name or mobile…"
                                    value={planProductSearch}
                                    onChange={e => { setPlanProductSearch(e.target.value); setPlanProductPage(50); }}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><People sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
                                    sx={{ flex: 1, minWidth: 0 }}
                                />
                                <SortSelect value={planSort} options={CUSTOMER_SORT_OPTIONS}
                                    onChange={v => { setPlanSort(v); setPlanProductPage(50); }} />
                            </Box>
                            {/* Filter chips */}
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.75 }}>
                                {[
                                    { value: null, label: 'All' },
                                    { value: 'no_plans', label: 'No plans' },
                                    { value: 'other_products', label: 'Other plans' },
                                ].map(opt => (
                                    <Chip key={opt.value ?? 'all'} label={opt.label} size="small" clickable
                                        color={planFilterBy === opt.value ? 'primary' : 'default'}
                                        variant={planFilterBy === opt.value ? 'filled' : 'outlined'}
                                        onClick={() => { setPlanFilterBy(opt.value); setPlanProductPage(50); setPlanSelectedIds([]); }}
                                    />
                                ))}
                            </Box>
                            {/* Areas on one line, as on Customers / Plans / Daily Ops — add a whole round at once */}
                            <AreaFilterRow
                                options={areaOptions}
                                noArea={{ key: PLAN_NO_AREA, count: eligible.filter(c => !c.f_area_id).length }}
                                total={eligible.length}
                                value={planAreaFilter}
                                onChange={key => { setPlanAreaFilter(key); setPlanProductPage(50); setPlanSelectedIds([]); }}
                                sx={{ mb: 0.75 }}
                            />
                            {/* A real row, not a caption with a ▸ glyph — that was too small to
                                see or hit on a phone. Counts from the same list it opens, so the
                                number and what you see when you tap it always agree. */}
                            {assignedCustomers.length > 0 && (
                                <Box
                                    component="button" type="button"
                                    onClick={() => setPlanAssignedExpanded(v => !v)}
                                    aria-expanded={planAssignedExpanded}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1, width: '100%',
                                        minHeight: TOUCH.min, px: 1.25, mb: 0.75,
                                        border: '1px solid', borderColor: 'primary.light', borderRadius: 1.5,
                                        bgcolor: planAssignedExpanded ? 'action.selected' : 'background.paper',
                                        color: 'text.primary', font: 'inherit', textAlign: 'left', cursor: 'pointer',
                                    }}
                                >
                                    <People fontSize="small" color="primary" />
                                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
                                        Already added ({assignedCustomers.length})
                                    </Typography>
                                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                                        {planAssignedExpanded ? 'Hide' : 'Show'}
                                    </Typography>
                                    {planAssignedExpanded
                                        ? <ExpandLess fontSize="small" color="primary" />
                                        : <ExpandMore fontSize="small" color="primary" />}
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {unassigned.length} available to add
                                </Typography>
                                {unassigned.length > 0 && (
                                    <Button size="small" sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0, minWidth: 0 }}
                                        onClick={() => allSelected ? setPlanSelectedIds([]) : setPlanSelectedIds(allUnassignedIds)}>
                                        {allSelected ? 'Deselect all' : 'Select all'}
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        {planAssignedExpanded && assignedCustomers.length > 0 && (
                            <Box sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                                {visibleAssigned.map(c => {
                                    const areaName = businessAreas.find(x => x.f_id === c.f_area_id)?.f_name;
                                    const agentName = businessMembers.find(m => m.f_id === c.f_agent_id)?.f_name;
                                    return (
                                        <Box key={c.f_id} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            px: 2, py: 1,
                                            borderBottom: '1px solid', borderColor: 'divider',
                                            bgcolor: 'action.hover',
                                            opacity: 0.75,
                                        }}>
                                            <Chip size="small" label="✓" color="primary" variant="outlined"
                                                sx={{ width: 32, height: 24, flexShrink: 0 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={500} noWrap>{c.f_name}</Typography>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', overflow: 'hidden' }}>
                                                    <Typography variant="caption" color="text.secondary" noWrap>{c.f_mobile}</Typography>
                                                    {c.f_address_line1 && <Typography variant="caption" color="text.disabled" noWrap>· {c.f_address_line1}</Typography>}
                                                    {areaName && <Typography variant="caption" color="text.disabled" noWrap>· {areaName}</Typography>}
                                                    {agentName && <Typography variant="caption" color="text.disabled" noWrap>· {agentName}</Typography>}
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                                {hasMoreAssigned && (
                                    <Box sx={{ py: 1.5, textAlign: 'center', bgcolor: 'action.hover' }}>
                                        <Button variant="outlined" size="small" onClick={() => setPlanAssignedPage(p => p + 50)}>
                                            Load more ({assignedCustomers.length - planAssignedPage} remaining)
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                        {planProductLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : unassigned.length === 0 ? (
                            <Box sx={{ py: 5, textAlign: 'center' }}>
                                <People sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    {planProductSearch ? 'No matches found' : 'All customers are already on this plan'}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {visibleUnassigned.map(c => {
                                    const isSelected = planSelectedIds.includes(c.f_id);
                                    const areaName = businessAreas.find(x => x.f_id === c.f_area_id)?.f_name;
                                    const agentName = businessMembers.find(m => m.f_id === c.f_agent_id)?.f_name;
                                    const isInactive = c.f_status && c.f_status !== 'Active';
                                    return (
                                        <Box key={c.f_id}
                                            onClick={() => {
                                                if (planAdding) return;
                                                setPlanSelectedIds(prev =>
                                                    isSelected ? prev.filter(id => id !== c.f_id) : [...prev, c.f_id]
                                                );
                                            }}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                px: 2, py: 1.25,
                                                borderBottom: '1px solid', borderColor: 'divider',
                                                cursor: planAdding ? 'default' : 'pointer',
                                                bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                                opacity: planAdding ? 0.7 : isInactive ? 0.6 : 1,
                                                userSelect: 'none',
                                                '&:active': { bgcolor: 'action.selected' },
                                            }}>
                                            <Chip size="small"
                                                label={isSelected ? '✓' : '+'}
                                                color={isSelected ? 'primary' : 'default'}
                                                variant={isSelected ? 'filled' : 'outlined'}
                                                sx={{ width: 32, height: 24, fontSize: '0.8rem', cursor: 'inherit', flexShrink: 0 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="body2" fontWeight={500} noWrap>{c.f_name}</Typography>
                                                    {isInactive && <Chip label={c.f_status} size="small" sx={{ fontSize: '0.65rem', height: 16, flexShrink: 0 }} />}
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', overflow: 'hidden' }}>
                                                    {planSort === SORT_NEWEST && addedLabel(c) && (
                                                        <Typography variant="caption" color="primary.main" fontWeight={700} noWrap sx={{ flexShrink: 0 }}>{addedLabel(c)} ·</Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.secondary" noWrap>{c.f_mobile}</Typography>
                                                    {c.f_address_line1 && <Typography variant="caption" color="text.disabled" noWrap>· {c.f_address_line1}</Typography>}
                                                    {areaName && <Typography variant="caption" color="text.disabled" noWrap>· {areaName}</Typography>}
                                                    {agentName && <Typography variant="caption" color="text.disabled" noWrap>· {agentName}</Typography>}
                                                </Box>
                                            </Box>
                                            <Box
                                                onClick={e => e.stopPropagation()}
                                                sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
                                            >
                                                <IconButton size="small" disabled={planAdding || (planCustomerQty[c.f_id] ?? planQuantity) <= 1}
                                                    onClick={() => setPlanCustomerQty(prev => ({ ...prev, [c.f_id]: Math.max(1, (prev[c.f_id] ?? planQuantity) - 1) }))}
                                                    sx={{ borderRadius: 0, px: 0.75, py: 0.5 }}>
                                                    <Remove sx={{ fontSize: 14 }} />
                                                </IconButton>
                                                <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center', fontWeight: 500, userSelect: 'none' }}>
                                                    {planCustomerQty[c.f_id] ?? planQuantity}
                                                </Typography>
                                                <IconButton size="small" disabled={planAdding}
                                                    onClick={() => setPlanCustomerQty(prev => ({ ...prev, [c.f_id]: (prev[c.f_id] ?? planQuantity) + 1 }))}
                                                    sx={{ borderRadius: 0, px: 0.75, py: 0.5 }}>
                                                    <Add sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    );
                                })}
                                {hasMore && (
                                    <Box sx={{ py: 2, textAlign: 'center' }}>
                                        <Button variant="outlined" size="small" onClick={() => setPlanProductPage(p => p + 50)}
                                            sx={{ textTransform: 'none' }}>
                                            Load more ({sortedUnassigned.length - planProductPage} remaining)
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                        {planAddResult && (
                            <Box sx={{ px: 2, pb: 1.5 }}>
                                <Alert severity={planAddResult.failed > 0 ? 'warning' : 'success'} sx={{ mt: 1 }}>
                                    {planAddResult.added} customer{planAddResult.added !== 1 ? 's' : ''} added
                                    {planAddResult.failed > 0 ? `, ${planAddResult.failed} failed` : ' successfully'}.
                                </Alert>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ gap: 1, px: 2, pb: 2 }}>
                        <Button onClick={closePlanDialog} disabled={planAdding} sx={{ textTransform: 'none' }}>Close</Button>
                        <Button
                            variant="contained" disableElevation
                            disabled={planSelectedIds.length === 0 || planAdding || !planStartDate}
                            onClick={handleBulkAddPlans}
                            startIcon={planAdding ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ textTransform: 'none', flex: 1 }}
                        >
                            {planAdding ? 'Adding…' : `Add Customers (${planSelectedIds.length})`}
                        </Button>
                    </DialogActions>
                </Dialog>
            );
        })()}

        {/* Image preview modal */}
        <Dialog open={!!previewImg} onClose={() => setPreviewImg(null)} maxWidth="sm" fullWidth>
            <DialogContent sx={{ p: 1, textAlign: 'center' }}>
                <Box component="img" src={previewImg} alt="preview"
                    sx={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 2, objectFit: 'contain' }} />
            </DialogContent>
        </Dialog>

        </>
    );
};

export default Dashboard;